import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import api, { API_URL } from '../api/api';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useDepressionMode } from '../contexts/DepressionModeContext';
import { AdBanner } from './GoogleAdsense';
import '../styles/tsumugi-design.css';
import '../styles/components/TsumugiChat.css';
import HamburgerMenu from './HamburgerMenu';
import UpgradeModal from './UpgradeModal';

/**
 * つむぎ会話コンポーネント
 * DEPRESSION_MODE_REDESIGN.md準拠: おやすみモード時は音声優先UI
 *
 * 特徴:
 * - 共感と寄り添いのAI会話
 * - シンプルで疲れないUI
 * - 音声入力対応
 * - おやすみモード時: 音声優先、テキストは折りたたみ
 * - 会話履歴保持
 */
const MAX_MESSAGE_LENGTH = 500; // 無料プラン: 500文字制限

/**
 * Mode 1（つむぎと話したい）固定応答マップ
 * 導入部分のみ固定応答、それ以降はAPI呼び出し
 * 設計書: docs/current/MODE2_API_OPTIMIZATION.md
 */
const MODE1_FIXED_RESPONSES = {
  // 初回のみ固定応答（API呼び出し1回削減）
  'つむぎと話したい': {
    empathy: 'うん、いいよ。',
    followUp: {
      question: '今日はどんな感じ？',
      suggestedActions: ['仕事で疲れた', '体調がしんどい', 'なんとなくモヤモヤ']
    }
  },
  // 2回目: 固定応答 + 選択肢（補助輪）
  '仕事で疲れた': {
    empathy: 'お仕事お疲れさま。',
    followUp: {
      question: '何かあった？それとも、ただ疲れた感じ？',
      suggestedActions: ['嫌なことがあった', 'ただ疲れた', '話すほどでもない']
    }
  },
  '体調がしんどい': {
    empathy: 'しんどいんだね。無理しなくていいよ。',
    followUp: {
      question: 'どんな感じ？',
      suggestedActions: ['だるくて動けない', '頭がぼーっとする', 'なんか調子悪い']
    }
  },
  'なんとなくモヤモヤ': {
    empathy: 'モヤモヤするよね。',
    followUp: {
      question: '何かあった？なくても大丈夫だよ。',
      suggestedActions: ['話したいことがある', '特にないけど聞いてほしい', 'よくわからない']
    }
  },
  // 3回目: 固定応答 + 選択肢（補助輪継続）
  '嫌なことがあった': {
    empathy: 'そっか、嫌なことあったんだね。',
    followUp: {
      question: '聞くよ。',
      suggestedActions: ['仕事の人間関係', '仕事の量が多い', '他のこと']
    }
  },
  'ただ疲れた': {
    empathy: 'うん、疲れたよね。',
    affirmation: '今日も頑張ったんだね。',
    followUp: {
      suggestedActions: ['うん、頑張った', '全然頑張れてない', '早く寝たい']
    }
  },
  '話すほどでもない': {
    empathy: 'うん、そういう時あるよね。',
    affirmation: 'ここにいるだけでいいよ。',
    followUp: {
      suggestedActions: ['ありがとう', 'ちょっと楽になった', '何か話そうかな']
    }
  },
  'だるくて動けない': {
    empathy: 'だるいよね。動けない日あるよ。',
    affirmation: '無理しなくていいよ。',
    followUp: {
      suggestedActions: ['横になってる', '何もできない', '少しマシになりたい']
    }
  },
  '頭がぼーっとする': {
    empathy: 'ぼーっとするの辛いよね。ブレインフォグかも。',
    followUp: {
      suggestedActions: ['集中できない', '言葉が出てこない', 'よくある']
    }
  },
  'なんか調子悪い': {
    empathy: 'なんか調子悪い、わかる。',
    followUp: {
      question: '言葉にしにくいよね。',
      suggestedActions: ['うまく言えない', '体が重い', '気分が沈む']
    }
  },
  '話したいことがある': {
    empathy: 'うん、聞くよ。',
    followUp: {
      question: '何でも話して。',
      suggestedActions: []  // ここは自由入力を促す
    },
    promptFreeInput: true
  },
  '特にないけど聞いてほしい': {
    empathy: 'うん、いるよ。',
    affirmation: '話さなくても大丈夫。',
    followUp: {
      suggestedActions: ['ありがとう', 'なんか楽になった', 'やっぱり話そうかな']
    }
  },
  'よくわからない': {
    empathy: 'わからない時あるよね。',
    affirmation: 'それでいいよ。',
    followUp: {
      suggestedActions: ['うん', 'なんかスッキリしない', '少し話してみる']
    }
  }
  // 4回目以降: API応答 + API生成の選択肢
};

/**
 * Mode 1でAPI呼び出しが必要な時点で表示する方向性選択
 * ユーザーの意図を明確にして、より適切な応答を提供するため
 */
const DIRECTION_CHOICES = [
  { label: 'ただ聞いて', type: 'venting' },
  { label: '一緒に考えたい', type: 'information-seeking' }
];

/**
 * Mode 3（体調の相談）固定応答マップ
 * 症状ヒアリング → FormPageV2への自然な橋渡し
 * 設計書: docs/current/SESSION_CONTEXT.md
 */
const MODE3_FIXED_RESPONSES = {
  // ========================================
  // 初回: 「体調の相談」ボタン押下時
  // ========================================
  'mode3_start': {
    empathy: 'どんな症状が気になる？',
    followUp: {
      suggestedActions: ['疲労・だるさ', 'むくみ・冷え', 'その他の症状', 'よくわからない']
    }
  },
  // 症状がよくわからない場合
  'よくわからない': {
    empathy: 'よくわからない時あるよね。橋本病は色んな症状が出るから。',
    followUp: {
      question: '一番困ってることは？',
      suggestedActions: ['とにかく疲れる', '体が重い・むくむ', 'つむぎと話したい']
    }
  },
  'とにかく疲れる': {
    empathy: 'とにかく疲れるんだね。それが一番辛いよね。',
    affirmation: '栄養面から詳しく見てみることもできるけど、どうする？',
    followUp: {
      suggestedActions: ['詳しく相談する', '今日は話を聞いてほしいだけ']
    },
    symptomPreset: 'morning_fatigue'
  },
  '体が重い・むくむ': {
    empathy: '体が重い感じ、辛いよね。',
    affirmation: '栄養面から詳しく見てみることもできるけど、どうする？',
    followUp: {
      suggestedActions: ['詳しく相談する', '今日は話を聞いてほしいだけ']
    },
    symptomPreset: 'swelling_cold'
  },

  // ========================================
  // 疲労・だるさ フロー（深掘り会話）
  // ========================================
  '疲労・だるさ': {
    empathy: '疲労感、辛いよね。',
    followUp: {
      question: 'どんな時が特にしんどい？',
      suggestedActions: ['朝起きられない', '午後から動けない', 'ずっとだるい']
    },
    symptomPreset: 'morning_fatigue'
  },
  '朝起きられない': {
    empathy: '朝がしんどいんだね。起き上がるのも一苦労だよね。',
    followUp: {
      question: '睡眠はどう？',
      suggestedActions: ['寝ても寝ても眠い', '夜眠れない', '途中で起きちゃう']
    }
  },
  '午後から動けない': {
    empathy: '午後からガクッとくるんだね。わかる。',
    followUp: {
      question: 'お昼ご飯の後とか？',
      suggestedActions: ['昼食後に特に', '夕方がピーク', '波がある']
    }
  },
  'ずっとだるい': {
    empathy: 'ずっとか...それは辛いね。',
    followUp: {
      question: 'いつ頃から？',
      suggestedActions: ['最近ひどくなった', 'ずっと前から', '波がある']
    }
  },
  // 疲労の深掘り2段目
  '寝ても寝ても眠い': {
    empathy: 'いくら寝ても足りないよね。橋本病あるあるだよ。',
    affirmation: '栄養面から詳しく見てみることもできるけど、どうする？',
    followUp: {
      suggestedActions: ['詳しく相談する', '今日は話を聞いてほしいだけ']
    }
  },
  '夜眠れない': {
    empathy: '眠れないの辛いよね。疲れてるのに眠れないって最悪だよね。',
    affirmation: '栄養面から詳しく見てみることもできるけど、どうする？',
    followUp: {
      suggestedActions: ['詳しく相談する', '今日は話を聞いてほしいだけ']
    }
  },
  '途中で起きちゃう': {
    empathy: '途中で目が覚めちゃうんだね。それだと疲れ取れないよね。',
    affirmation: '栄養面から詳しく見てみることもできるけど、どうする？',
    followUp: {
      suggestedActions: ['詳しく相談する', '今日は話を聞いてほしいだけ']
    }
  },
  '昼食後に特に': {
    empathy: '昼食後にドーンとくるよね。血糖値の関係もあるかも。',
    affirmation: '栄養面から詳しく見てみることもできるけど、どうする？',
    followUp: {
      suggestedActions: ['詳しく相談する', '今日は話を聞いてほしいだけ']
    }
  },
  '夕方がピーク': {
    empathy: '夕方が一番しんどいんだね。夕飯作るのも辛いよね。',
    affirmation: '栄養面から詳しく見る？それとも今日の夕飯、一緒に考える？',
    followUp: {
      suggestedActions: ['詳しく相談する', '夕飯を考えたい', '今日は話を聞いてほしいだけ']
    }
  },
  // 夕飯を考えたい → Mode 2へ誘導
  '夕飯を考えたい': {
    empathy: 'うん、一緒に考えよう。',
    notes: '食事の相談に移動するね。',
    navigateTo: '/tsumugi?mode=meal',
    navigateLabel: '🍽️ 食事の相談へ'
  },
  '最近ひどくなった': {
    empathy: '最近ひどくなったんだね。何かきっかけがあったのかも。',
    affirmation: '栄養面から詳しく見てみることもできるけど、どうする？',
    followUp: {
      suggestedActions: ['詳しく相談する', '今日は話を聞いてほしいだけ']
    }
  },
  'ずっと前から': {
    empathy: 'ずっと付き合ってきたんだね。それだけでも頑張ってるよ。',
    affirmation: '栄養面から詳しく見てみることもできるけど、どうする？',
    followUp: {
      suggestedActions: ['詳しく相談する', '今日は話を聞いてほしいだけ']
    }
  },
  '波がある': {
    empathy: '波があるんだね。調子いい時と悪い時の差が辛いよね。',
    affirmation: '栄養面から詳しく見てみることもできるけど、どうする？',
    followUp: {
      suggestedActions: ['詳しく相談する', '今日は話を聞いてほしいだけ']
    }
  },

  // ========================================
  // むくみ・冷え フロー（深掘り会話）
  // ========================================
  'むくみ・冷え': {
    empathy: 'むくみや冷え、辛いよね。',
    followUp: {
      question: 'どっちが気になる？',
      suggestedActions: ['むくみがひどい', '冷えがひどい', '両方']
    },
    symptomPreset: 'swelling_cold'
  },
  'むくみがひどい': {
    empathy: 'むくみか。顔？足？',
    followUp: {
      suggestedActions: ['顔がパンパン', '足がパンパン', '全体的に']
    }
  },
  '冷えがひどい': {
    empathy: '冷えか。手足？お腹？',
    followUp: {
      suggestedActions: ['手足が冷たい', 'お腹が冷える', '全身が冷える']
    }
  },
  '両方': {
    empathy: '両方か...それは大変だね。橋本病だとよくあるんだよね。',
    affirmation: '栄養面から詳しく見てみることもできるけど、どうする？',
    followUp: {
      suggestedActions: ['詳しく相談する', '今日は話を聞いてほしいだけ']
    }
  },
  // むくみの深掘り
  '顔がパンパン': {
    empathy: '朝起きた時とか特にひどい？鏡見るの嫌になるよね。',
    affirmation: '栄養面から詳しく見てみることもできるけど、どうする？',
    followUp: {
      suggestedActions: ['詳しく相談する', '今日は話を聞いてほしいだけ']
    }
  },
  '足がパンパン': {
    empathy: '夕方になると靴がきつくなったりする？辛いよね。',
    affirmation: '栄養面から詳しく見てみることもできるけど、どうする？',
    followUp: {
      suggestedActions: ['詳しく相談する', '今日は話を聞いてほしいだけ']
    }
  },
  '全体的に': {
    empathy: '全体的にむくむんだね。体が重く感じるよね。',
    affirmation: '栄養面から詳しく見てみることもできるけど、どうする？',
    followUp: {
      suggestedActions: ['詳しく相談する', '今日は話を聞いてほしいだけ']
    }
  },
  // 冷えの深掘り
  '手足が冷たい': {
    empathy: '手足の冷え、辛いよね。夏でも冷たかったりする？',
    affirmation: '栄養面から詳しく見てみることもできるけど、どうする？',
    followUp: {
      suggestedActions: ['詳しく相談する', '今日は話を聞いてほしいだけ']
    }
  },
  'お腹が冷える': {
    empathy: 'お腹の冷えか。内臓が冷えると全身に影響するよね。',
    affirmation: '栄養面から詳しく見てみることもできるけど、どうする？',
    followUp: {
      suggestedActions: ['詳しく相談する', '今日は話を聞いてほしいだけ']
    }
  },
  '全身が冷える': {
    empathy: '全身が冷えるの辛いね。布団に入っても温まらなかったりする？',
    affirmation: '栄養面から詳しく見てみることもできるけど、どうする？',
    followUp: {
      suggestedActions: ['詳しく相談する', '今日は話を聞いてほしいだけ']
    }
  },

  // ========================================
  // その他の症状 フロー
  // ========================================
  'その他の症状': {
    empathy: '他にも色々あるよね。',
    followUp: {
      question: 'どんな感じ？',
      suggestedActions: ['頭がぼーっとする', '気分が落ち込む', '他のこと']
    },
    symptomPreset: 'other'
  },
  '頭がぼーっとする': {
    empathy: '頭がぼーっとするの辛いよね。集中できないし。',
    affirmation: '栄養面から詳しく見てみることもできるけど、どうする？',
    followUp: {
      suggestedActions: ['詳しく相談する', '今日は話を聞いてほしいだけ']
    }
  },
  '気分が落ち込む': {
    empathy: '気分が落ち込むの、辛いよね。体の不調と気持ちってつながってるから。',
    affirmation: '栄養面から詳しく見てみることもできるけど、どうする？',
    followUp: {
      suggestedActions: ['詳しく相談する', '今日は話を聞いてほしいだけ']
    }
  },
  '他のこと': {
    empathy: 'そっか。聞かせてね。',
    followUp: {
      suggestedActions: ['詳しく相談する', '今日は話を聞いてほしいだけ']
    },
    enableFreeInput: true
  },

  // ========================================
  // 終了フロー
  // ========================================
  // FormPageV2への遷移（予告メッセージ + ボタン）
  '詳しく相談する': {
    empathy: 'わかった。詳しく相談できる画面に移動するね。',
    notes: '症状や生活のこと、ゆっくり入力してね。終わったらまた話そう。',
    navigateTo: '/form-v2',
    navigateLabel: '💊 相談画面へ',
    useSymptomPreset: true
  },
  // 話を聞いてほしいだけの場合 → 会話継続
  '今日は話を聞いてほしいだけ': {
    empathy: 'うん、わかった。話聞くよ。',
    followUp: {
      question: '他に気になることある？',
      suggestedActions: ['他の症状がある', 'つむぎと話したい', '今日はここまで']
    }
  },
  // 他の症状がある → 最初に戻る
  '他の症状がある': {
    empathy: 'そっか。他にもあるんだね。',
    followUp: {
      question: 'どんな症状？',
      suggestedActions: ['疲労・だるさ', 'むくみ・冷え', 'その他の症状']
    }
  },
  // つむぎと話したい → Mode 1的な会話へ
  'つむぎと話したい': {
    empathy: 'うん、話そう。何かあった？',
    followUp: {
      suggestedActions: ['仕事のこと', '体調のこと', 'なんとなく']
    },
    enableFreeInput: true
  },
  // 「つむぎと話したい」からの分岐（傾聴ベース）
  '仕事のこと': {
    empathy: '仕事か...何かあった？',
    followUp: {
      suggestedActions: ['人間関係', '仕事量が多い', '体調との両立が辛い']
    },
    enableFreeInput: true
  },
  '体調のこと': {
    empathy: '体調のこと、話してくれてありがとう。',
    followUp: {
      question: 'どんな感じ？',
      suggestedActions: ['疲労・だるさ', 'むくみ・冷え', 'その他の症状']
    }
  },
  'なんとなく': {
    empathy: 'なんとなくか。そういう時あるよね。\n\n話したくなったら話してね。ここにいるから。',
    followUp: {
      suggestedActions: ['ちょっと話したいかも', '今日はここまで']
    },
    enableFreeInput: true
  },
  // 仕事の深掘り
  '人間関係': {
    empathy: '人間関係か...それは疲れるよね。',
    affirmation: '話してくれてありがとう。',
    followUp: {
      suggestedActions: ['もう少し話したい', '今日はここまで']
    },
    enableFreeInput: true
  },
  '仕事量が多い': {
    empathy: '仕事量が多いと、体にも心にもくるよね。',
    affirmation: '橋本病だと余計に辛いよね。',
    followUp: {
      suggestedActions: ['もう少し話したい', '今日はここまで']
    },
    enableFreeInput: true
  },
  '体調との両立が辛い': {
    empathy: '体調と仕事の両立、本当に大変だよね。',
    affirmation: '頑張ってるね。',
    followUp: {
      suggestedActions: ['もう少し話したい', '今日はここまで']
    },
    enableFreeInput: true
  },
  // 続けて話す
  'ちょっと話したいかも': {
    empathy: 'うん、聞くよ。何でも話してね。',
    followUp: {
      suggestedActions: ['仕事のこと', '体調のこと', '今日はここまで']
    },
    enableFreeInput: true
  },
  'もう少し話したい': {
    empathy: 'うん、聞いてるよ。何でも話してね。',
    followUp: {
      suggestedActions: ['仕事のこと', '体調のこと', '今日はここまで']
    },
    enableFreeInput: true
  },
  // 今日はここまで
  '今日はここまで': {
    empathy: 'うん、お疲れ様。\n\n無理しないでね。また話そうね。',
    isEnding: true
  },
  // 旧「今はいい」→ 会話継続へ変更
  '今はいい': {
    empathy: 'うん、わかった。',
    followUp: {
      question: '他に気になることある？',
      suggestedActions: ['他の症状がある', 'つむぎと話したい', '今日はここまで']
    }
  }
};

/**
 * Mode 2（食事の相談）固定応答マップ
 * API呼び出しを削減するため、分岐確認フェーズは固定応答で対応
 * 設計書: docs/current/MODE2_API_OPTIMIZATION.md
 */
const MODE2_FIXED_RESPONSES = {
  // 初回
  'mode2_start': {
    empathy: '今日は何食べようか。',
    followUp: {
      question: 'どんな感じ？',
      suggestedActions: ['冷蔵庫に食材ある', '買い物に行ける', '考えるのもしんどい']
    }
  },
  // パターンA
  '冷蔵庫に食材ある': {
    empathy: 'あるんだね。',
    conversation: '写真撮ってくれたら、その食材でレシピ考えるよ。ボタンで食材選ぶのもOK。',
    followUp: {
      question: 'どっちがいい？',
      suggestedActions: ['写真を撮る', 'ボタンで選ぶ', 'やっぱりつむぎに考えてほしい']
    }
  },
  // パターンB
  '買い物に行ける': {
    empathy: 'いいね、行けるんだ。',
    followUp: {
      question: 'どうする？',
      suggestedActions: ['チラシ・特売品を撮影', 'おすすめ食材を教えて', '何買えばいいかわからない']
    }
  },
  // パターンC
  '考えるのもしんどい': {
    empathy: 'わかる。考えるのもしんどいよね。',
    followUp: {
      question: 'どのくらい動ける？',
      suggestedActions: ['コンビニなら行ける', '家から出たくない', '10分なら料理できる']
    }
  },
  // 疲労度別（コンビニ）
  'コンビニなら行ける': {
    empathy: 'コンビニなら、これがおすすめだよ。',
    foods: [
      { name: 'サラダチキン', reason: 'タンパク質たっぷり' },
      { name: 'ゆで卵', reason: '手軽にタンパク質' },
      { name: 'おにぎり（鮭）', reason: 'セレンが取れる' }
    ],
    followUp: {
      suggestedActions: ['他のおすすめある？', 'やっぱり家で何か作りたい']
    }
  },
  // 疲労度別（家）
  '家から出たくない': {
    empathy: '無理しなくていいよ。家にあるもので。',
    foods: [
      { name: '納豆ごはん', reason: 'タンパク質と発酵食品' },
      { name: '卵かけごはん', reason: '簡単にタンパク質' },
      { name: 'バナナ', reason: 'エネルギー補給' }
    ],
    followUp: {
      suggestedActions: ['他に何かある？', 'それでいい']
    }
  },
  // 10分なら料理できる - DBからレシピ取得
  '10分なら料理できる': {
    fetchRecipes: true,
    fatigueLevel: 'moderate',
    empathy: '10分あるなら、これどう？',
    followUp: {
      suggestedActions: ['他のレシピが見たい', 'やっぱり作れないかも']
    }
  },
  // 追加アクション
  'やっぱり作れないかも': {
    empathy: 'そっか、そういう日あるよね。無理しなくていいよ。',
    followUp: {
      question: '今日はどうする？無理しなくていいからね。',
      suggestedActions: ['コンビニで済ませる', '家にあるもので何とかする', '今日は食べなくてもいいかな']
    },
    enableFreeInput: true
  },
  // パターンD-1: コンビニで済ませる
  'コンビニで済ませる': {
    empathy: 'うん、それでいいよ。サラダチキンとおにぎりとか、それだけでも十分。',
    affirmation: '頑張ったね。',
    foods: [
      { name: 'サラダチキン', reason: 'タンパク質補給' },
      { name: 'おにぎり（昆布以外）', reason: 'エネルギー' }
    ],
    followUp: {
      suggestedActions: []
    },
    isEnding: true
  },
  // パターンD-2: 家にあるもので
  '家にあるもので何とかする': {
    empathy: 'えらいね。納豆ごはんとか、卵かけごはんで十分だよ。',
    foods: [
      { name: '納豆ごはん', reason: 'タンパク質も摂れる' },
      { name: '卵かけごはん', reason: 'すぐ食べられる' }
    ],
    followUp: {
      suggestedActions: []
    },
    isEnding: true
  },
  // パターンD-3: 食べなくてもいい
  '今日は食べなくてもいいかな': {
    empathy: '無理しないでね。でもちょっとだけ、バナナとか水分だけでも取れたらいいな。',
    notes: 'また明日、ここにいるからね。',
    followUp: {
      suggestedActions: []
    },
    isEnding: true
  },
  'やっぱりつむぎに考えてほしい': {
    fetchRecipes: true,
    fatigueLevel: 'low',
    empathy: 'わかった、任せて。これどうかな？',
    followUp: {
      suggestedActions: ['他のレシピが見たい', 'やっぱり作れないかも']
    }
  },
  '他のレシピが見たい': {
    fetchRecipes: true,
    excludePrevious: true,
    empathy: 'じゃあこれは？',
    followUp: {
      suggestedActions: ['他のレシピが見たい', 'やっぱり作れないかも']
    }
  },
  '他のおすすめある？': {
    empathy: 'うん、他にもあるよ。',
    foods: [
      { name: 'ヨーグルト', reason: '腸内環境に良い' },
      { name: '豆乳', reason: '植物性タンパク質' },
      { name: 'チーズ', reason: 'カルシウム補給' }
    ],
    followUp: {
      suggestedActions: ['これでいいかも', 'やっぱり家で何か作りたい']
    }
  },
  '他に何かある？': {
    empathy: 'うん、他にもあるよ。',
    foods: [
      { name: '味噌汁（インスタント）', reason: '発酵食品で体を温める' },
      { name: 'ヨーグルト', reason: '腸内環境に良い' },
      { name: 'チーズトースト', reason: 'タンパク質とカルシウム' }
    ],
    followUp: {
      suggestedActions: ['これでいいかも', 'もう少し頑張れるかも']
    }
  },
  // 料理へ移行
  'やっぱり家で何か作りたい': {
    empathy: 'いいね！どのくらい動けそう？',
    followUp: {
      suggestedActions: ['5分くらい', '10分くらい', '15分以上OK']
    }
  },
  'もう少し頑張れるかも': {
    empathy: 'えらい！どのくらい動けそう？',
    followUp: {
      suggestedActions: ['5分くらい', '10分くらい', '15分以上OK']
    }
  },
  '5分くらい': {
    fetchRecipes: true,
    fatigueLevel: 'high',
    empathy: '5分でできるレシピ、あるよ。',
    followUp: {
      suggestedActions: ['他のレシピが見たい', 'やっぱり作れないかも']
    }
  },
  '10分くらい': {
    fetchRecipes: true,
    fatigueLevel: 'moderate',
    empathy: '10分でできるレシピ、見つけたよ。',
    followUp: {
      suggestedActions: ['他のレシピが見たい', 'やっぱり作れないかも']
    }
  },
  '15分以上OK': {
    fetchRecipes: true,
    fatigueLevel: 'low',
    empathy: '時間あるんだね！しっかりしたレシピ、どう？',
    followUp: {
      suggestedActions: ['他のレシピが見たい', 'やっぱり作れないかも']
    }
  },
  // 買い物系
  'おすすめ食材を教えて': {
    empathy: '橋本病にいい食材、いくつかあるよ。',
    foods: [
      { name: 'ブラジルナッツ', reason: 'セレンが豊富' },
      { name: '鮭', reason: 'セレンとオメガ3' },
      { name: '卵', reason: 'タンパク質とセレン' }
    ],
    followUp: {
      suggestedActions: ['他にもある？', 'これ買ってくる']
    }
  },
  '何買えばいいかわからない': {
    empathy: '迷うよね。とりあえずこれ買っとくといいよ。',
    foods: [
      { name: '卵', reason: '万能食材、タンパク質' },
      { name: '豆腐', reason: '安くてヘルシー' },
      { name: 'バナナ', reason: 'すぐ食べられる' }
    ],
    followUp: {
      suggestedActions: ['他にもある？', 'これ買ってくる']
    }
  },
  // 追加の食材リスト
  '他にもある？': {
    empathy: 'うん、他にもあるよ。',
    foods: [
      { name: 'アボカド', reason: '良質な脂質とビタミンE' },
      { name: 'ほうれん草', reason: '鉄分補給' },
      { name: 'きのこ類', reason: 'ビタミンDが豊富' }
    ],
    followUp: {
      suggestedActions: ['まだある？', 'これ買ってくる']
    }
  },
  'まだある？': {
    empathy: 'まだまだあるよ。',
    foods: [
      { name: 'さば', reason: 'オメガ3と良質なタンパク質' },
      { name: 'ひじき', reason: '鉄分とミネラル' },
      { name: 'くるみ', reason: 'オメガ3と抗酸化物質' }
    ],
    followUp: {
      suggestedActions: ['ありがとう、これ買ってくる', 'やっぱり家で何か作りたい']
    }
  },
  'ありがとう、これ買ってくる': {
    empathy: 'いってらっしゃい！\n\n無理しないでね。体調見ながらね。',
    isEnding: true
  },
  // 遷移（予告メッセージ + ボタン）
  '写真を撮る': {
    empathy: 'わかった。撮影画面に移動するね。',
    notes: '終わったらまた話そう。',
    navigateTo: '/fridge-photo',
    navigateLabel: '📷 撮影画面へ'
  },
  'ボタンで選ぶ': {
    empathy: 'わかった。食材選択画面に移動するね。',
    notes: '終わったらまた話そう。',
    navigateTo: '/fridge-recipe',
    navigateLabel: '🔍 食材を選ぶ'
  },
  'チラシ・特売品を撮影': {
    empathy: 'わかった。撮影画面に移動するね。',
    notes: '終わったらまた話そう。',
    navigateTo: '/flyer-recipe',
    navigateLabel: '🛒 撮影画面へ'
  },
  // 終了系
  'それでいい': {
    empathy: 'うん、それでいいよ。\n\n無理しないでね。また話そう。',
    isEnding: true
  },
  'これでいいかも': {
    empathy: 'よかった！\n\nゆっくり食べてね。また話そう。',
    isEnding: true
  },
  'これ買ってくる': {
    empathy: 'いってらっしゃい！\n\n無理しないでね。',
    isEnding: true
  }
};

/**
 * 固定応答からメッセージを生成
 * Mode 1: empathy + affirmation + conversation
 * Mode 2: empathy + conversation + foods/recipes
 */
const buildFixedResponse = (response, recipes = null) => {
  let content = response.empathy || '';

  // Mode 1: affirmation（肯定・共感メッセージ）
  if (response.affirmation) {
    content += '\n\n' + response.affirmation;
  }

  if (response.conversation) {
    content += '\n\n' + response.conversation;
  }

  // 食品リスト
  if (response.foods && response.foods.length > 0) {
    content += '\n\n';
    response.foods.forEach((food, index) => {
      content += `**${food.name}**\n${food.reason}\n\n`;
    });
  }

  // レシピリスト（DBから取得した場合）
  if (recipes && recipes.length > 0) {
    content += '\n\n';
    recipes.forEach((recipe) => {
      content += `**${recipe.name}**\n${recipe.reason || recipe.description}\n[レシピを見る: ${recipe.name}]\n\n`;
    });
  }

  // フォローアップ質問
  if (response.followUp?.question) {
    content += '\n' + response.followUp.question;
  }

  return content.trim();
};

const TsumugiChat = ({ userContext = {} }) => {
  const navigate = useNavigate();
  const { sessionId: urlSessionId } = useParams(); // URLパラメータからsessionIdを取得
  const [searchParams] = useSearchParams(); // クエリパラメータを取得
  const { user, plan } = useAuth(); // 認証情報を取得
  const { depressionMode } = useDepressionMode(); // おやすみモード状態を取得
  const userId = user?._id || user?.id || null;

  // オンボーディングからのパラメータ
  const isFromOnboarding = searchParams.get('onboarding') === 'true';
  const onboardingSituation = searchParams.get('situation'); // 'moderate', 'low', 'very_low' (疲労度ベース)
  // 新規ユーザーフラグ（登録直後）
  const isNewUserParam = searchParams.get('newUser') === 'true';
  const initialPlan = plan || 'free';
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isGeneratingRecipe, setIsGeneratingRecipe] = useState(false); // レシピ生成専用
  const [isTsumugiTyping, setIsTsumugiTyping] = useState(false);
  const [dailyUsage, setDailyUsage] = useState(0);
  const [dailyLimit, setDailyLimit] = useState(Infinity); // 日次制限撤廃済み
  const [isRecording, setIsRecording] = useState(false);
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const [sessionId, setSessionId] = useState(urlSessionId || null); // 現在のセッションID（URLパラメータから取得）
  const [showMenu, setShowMenu] = useState(false); // ハンバーガーメニュー表示状態
  const [expiringConversations, setExpiringConversations] = useState([]); // 削除予定の会話
  const [userPlan, setUserPlan] = useState(initialPlan); // propsから初期値を取得
  const [activeTab, setActiveTab] = useState('chat'); // 'chat' or 'history'
  const [sessions, setSessions] = useState([]); // 過去のセッション一覧
  const [selectedSession, setSelectedSession] = useState(null); // 選択されたセッション
  const [selectedMode, setSelectedMode] = useState('chat'); // 選択されたモード: 'chat', 'meal', 'symptom'（デフォルトはchat）
  const [showModeSelector, setShowModeSelector] = useState(false); // モード選択画面の表示状態（デフォルトfalse: Invisible Mode）
  const [mode2State, setMode2State] = useState(null); // Mode 2の現在の状態（固定応答フロー用）
  const [mode3State, setMode3State] = useState(null); // Mode 3の現在の状態（固定応答フロー用）
  const [lastSymptomPreset, setLastSymptomPreset] = useState(null); // 直前に選択された症状プリセット
  const [suggestedRecipeIds, setSuggestedRecipeIds] = useState([]); // 提案済みレシピID（除外用）
  const [showLimitModal, setShowLimitModal] = useState(false); // 使い切り時の優しいモーダル
  // ポイント枯渇時のアップグレード誘導モーダル
  const [showPointsExhaustedModal, setShowPointsExhaustedModal] = useState(false);
  const [pointsExhaustedInfo, setPointsExhaustedInfo] = useState({ remainingPoints: 0, requiredPoints: 1, monthlyPoints: null });
  const [lastApiCallInfo, setLastApiCallInfo] = useState(null); // Progressive Disclosure用: 最後のAPI呼び出し情報
  const [showResumePrompt, setShowResumePrompt] = useState(false); // 5分以上離脱時の「続きから話す？」プロンプト
  const [pendingResumeSessionId, setPendingResumeSessionId] = useState(null); // 復元候補のセッションID
  const [directionChosen, setDirectionChosen] = useState(false); // Mode 1: 方向性選択済みフラグ
  const [intentType, setIntentType] = useState('default'); // Mode 1: ユーザーの意図タイプ（venting, information-seeking, silent）
  const [pendingUserMessage, setPendingUserMessage] = useState(null); // 方向性選択待ちのユーザーメッセージ
  const [showDirectionChoice, setShowDirectionChoice] = useState(false); // 方向性選択UIの表示フラグ
  // 新規ユーザーウェルカム画面
  const [isNewUser, setIsNewUser] = useState(isNewUserParam);
  const [welcomeCompleted, setWelcomeCompleted] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const recognitionRef = useRef(null);
  const abortControllerRef = useRef(null); // ストリーミングリクエストのキャンセル用
  const isLoadingRef = useRef(false); // 同期的なローディング状態（Reactの非同期state更新を回避）

  /**
   * レシピボタンをクリックした時の処理
   * キャッシュチェック → なければレシピ生成・保存 → 詳細ページに遷移
   */
  const handleRecipeButtonClick = async (recipeName) => {
    // キャッシュチェック
    const cachedRecipeId = sessionStorage.getItem(`recipe-id-${recipeName}`);

    if (cachedRecipeId) {
      // キャッシュがある場合、レシピ詳細ページに直接遷移
      console.log(`✅ キャッシュされたレシピを使用: ${cachedRecipeId}`);

      // つむぎから来たことを記録（戻るボタンで使用）
      sessionStorage.setItem('recipeBackTo', 'tsumugi-chat');
      if (sessionId) {
        sessionStorage.setItem('tsumugiSessionId', sessionId);
      }

      navigate(`/recipes/${cachedRecipeId}`);
      return;
    }

    // キャッシュがない場合、新規生成
    setIsGeneratingRecipe(true);

    try {
      // 最新のユーザーメッセージから文脈を判定
      const lastUserMessage = messages.filter(m => m.sender === 'user').slice(-1)[0]?.text || '';
      const lowerMessage = lastUserMessage.toLowerCase();

      let context = 'fatigue'; // デフォルト: つむぎがレシピを提案する状況=体調不良と仮定
      if (lowerMessage.includes('風邪') || lowerMessage.includes('熱') || lowerMessage.includes('咳')) {
        context = 'cold';
      } else if (lowerMessage.includes('疲') || lowerMessage.includes('だるい') || lowerMessage.includes('しんどい')) {
        context = 'fatigue';
      }

      // 難易度を決定
      let difficulty = 'easy';
      if (depressionMode || context === 'fatigue' || context === 'cold') {
        difficulty = 'super_easy';
      }

      // ユーザーのNG食材を取得
      const allergies = user?.profile?.allergies || [];
      const avoidFoods = user?.profile?.avoidFoods || [];
      const dietaryRestrictionsText = user?.dietaryRestrictions?.medicalDietaryNeeds || '';

      // レシピ生成API呼び出し
      console.log(`🍳 レシピ生成中: ${recipeName} (難易度: ${difficulty})`);
      const generateResponse = await api.post('/api/recipes/generate-by-name', {
        recipeName: recipeName,
        preferences: [...allergies, ...avoidFoods], // NG食材を明示
        dietaryRestrictions: dietaryRestrictionsText,
        servings: 2,
        recipeDifficulty: difficulty
      });

      if (!generateResponse.data.success) {
        throw new Error('レシピ生成に失敗しました');
      }

      const recipeMarkdown = generateResponse.data.recipes;

      // タイトルを抽出
      const titleMatch = recipeMarkdown.match(/##\s+\d+\.\s+(.+)/);
      const recipeTitle = titleMatch ? titleMatch[1].trim() : recipeName;

      // タグを自動抽出
      const autoTags = [];
      const lowerContent = recipeMarkdown.toLowerCase();
      if (lowerContent.includes('ヨウ素控えめ') || lowerContent.includes('甲状腺') || lowerContent.includes('橋本病')) {
        autoTags.push('ヨウ素控えめ');
      }
      if (lowerContent.includes('セレン')) {
        autoTags.push('セレン');
      }
      if (lowerContent.includes('グルテンフリー')) {
        autoTags.push('グルテンフリー');
      }
      if (lowerContent.includes('簡単') || lowerContent.includes('時短')) {
        autoTags.push('簡単');
      }
      autoTags.push('つむぎおすすめ');

      // レシピ保存API呼び出し
      console.log(`💾 レシピ保存中: ${recipeTitle}`);
      const saveResponse = await api.post('/api/recipes/save', {
        title: recipeTitle,
        content: recipeMarkdown.trim(),
        sourceConsultationId: null,
        tags: autoTags
      });

      if (!saveResponse.data.success || !saveResponse.data.recipe?._id) {
        throw new Error('レシピ保存に失敗しました');
      }

      const savedRecipeId = saveResponse.data.recipe._id;

      // キャッシュに保存
      sessionStorage.setItem(`recipe-id-${recipeName}`, savedRecipeId);
      console.log(`✅ レシピを保存しました: ${recipeTitle} (ID: ${savedRecipeId})`);

      // つむぎから来たことを記録（戻るボタンで使用）
      sessionStorage.setItem('recipeBackTo', 'tsumugi-chat');
      if (sessionId) {
        sessionStorage.setItem('tsumugiSessionId', sessionId);
      }

      // レシピ詳細ページに遷移
      navigate(`/recipes/${savedRecipeId}`);

    } catch (error) {
      console.error('レシピ生成・保存エラー:', error);

      if (error.response?.status === 429) {
        alert('今月は上限までご利用いただきました。来月またご利用いただけます。');
      } else {
        alert('レシピの生成に失敗しました。もう一度お試しください。');
      }
    } finally {
      setIsGeneratingRecipe(false);
    }
  };

  /**
   * ReactMarkdownのカスタムコンポーネント
   * [レシピを見る: 料理名] をボタンとしてレンダリング
   * [QUICK_REPLY][...] をクイックリプライボタンとしてレンダリング
   */
  const markdownComponents = {
    p: ({ children, node, ...props }) => {
      // children が文字列の場合のみパース
      if (typeof children === 'string') {
        const recipeButtonPattern = /\[レシピを見る:\s*([^\]]+)\]/g;
        const quickReplyPattern = /\[QUICK_REPLY\](\[[^\]]+\])/g;
        const parts = [];
        let lastIndex = 0;

        // すべてのマッチを取得（レシピ + クイックリプライ）
        const allMatches = [];

        let match;
        while ((match = recipeButtonPattern.exec(children)) !== null) {
          allMatches.push({ type: 'recipe', match });
        }
        while ((match = quickReplyPattern.exec(children)) !== null) {
          allMatches.push({ type: 'quickReply', match });
        }

        // インデックス順にソート
        allMatches.sort((a, b) => a.match.index - b.match.index);

        allMatches.forEach(({ type, match }) => {
          // マッチ前のテキスト
          if (match.index > lastIndex) {
            parts.push(children.substring(lastIndex, match.index));
          }

          if (type === 'recipe') {
            // レシピボタン
            const recipeName = match[1].trim();
            parts.push(
              <button
                key={`recipe-btn-${match.index}`}
                className="tsumugi-recipe-button"
                onClick={() => handleRecipeButtonClick(recipeName)}
              >
                <span className="recipe-icon">🍳</span>
                レシピを見る: {recipeName}
              </button>
            );
          } else if (type === 'quickReply') {
            // クイックリプライは Mode別UI に統一するため、ここでは表示しない
            // [QUICK_REPLY][...] パターンをテキストから削除
            // （suggestedActions から mode別ボタンとして表示される）
          }

          lastIndex = match.index + match[0].length;
        });

        // マッチ後のテキスト
        if (lastIndex < children.length) {
          parts.push(children.substring(lastIndex));
        }

        // マッチがあった場合（QUICK_REPLYを削除した場合も含む）
        if (allMatches.length > 0) {
          // partsが空または空文字列のみの場合は何も表示しない
          const filteredParts = parts.filter(p => typeof p !== 'string' || p.trim() !== '');
          if (filteredParts.length === 0) {
            return null; // QUICK_REPLYのみの段落は非表示
          }
          return <p {...props}>{filteredParts}</p>;
        }
      }

      // デフォルトの段落レンダリング（nodeを除外）
      return <p {...props}>{children}</p>;
    }
  };

  // ページタイトル設定
  useEffect(() => {
    document.title = 'つむぎと話す | つむぎ';
  }, []);

  // コンポーネントアンマウント時にリクエストをキャンセル
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // 初回メッセージの取得 & 会話履歴ロード
  useEffect(() => {
    // sessionIdをURLパラメータの値に同期
    setSessionId(urlSessionId || null);
    setMessages([]); // メッセージをクリア
    setActiveTab('chat'); // チャットタブに戻す

    loadConversationHistory();
    if (userId) {
      checkExpiringConversations();
      loadSessions(); // セッション一覧を取得
    }
  }, [userId, urlSessionId, isFromOnboarding, onboardingSituation, searchParams]); // urlSessionId, オンボーディングパラメータ, クエリパラメータの変化を監視

  // 使い方ガイドからの例文セット
  useEffect(() => {
    const prompt = searchParams.get('prompt');
    if (prompt) {
      setInput(prompt);
    }
  }, [searchParams]);

  // プラン変更時にuserPlanを更新
  useEffect(() => {
    if (initialPlan && initialPlan !== userPlan) {
      setUserPlan(initialPlan);
    }
    // 日次制限は全プラン撤廃済み（ポイント制で管理）
  }, [initialPlan]);

  // 自動スクロール（メッセージ追加時）
  useEffect(() => {
    // メッセージが追加されたら常にスクロール（LINE風）
    // ユーザーが送信した時もつむぎの返信時も両方スクロール
    if (messages.length > 0) {
      setTimeout(() => {
        scrollToBottom();
      }, 100);
    }
  }, [messages]);

  // セッション情報をsessionStorageに保存（ハイブリッド復元用）
  useEffect(() => {
    // sessionIdがあり、メッセージが2つ以上（初期メッセージ + ユーザー入力）の場合に保存
    if (sessionId && messages.length >= 2) {
      const sessionData = {
        sessionId,
        lastActivity: Date.now(),
        messages: messages.slice(-20) // 最新20件のみ保存（容量節約）
      };
      sessionStorage.setItem('tsumugi_active_session', JSON.stringify(sessionData));
      console.log('💾 セッション情報を保存:', sessionId);
    }
  }, [sessionId, messages]);

  // 会話履歴をロード（セッション復元ロジック含む）
  const loadConversationHistory = async () => {
    // クエリパラメータから新規/続きのフラグを取得
    const isNewSession = searchParams.get('new') === 'true';
    const isContinueSession = searchParams.get('continue') === 'true';

    // 「新しく話す」ボタンから来た場合 → 強制的に新規会話
    if (isNewSession) {
      console.log('✨ 新規会話モード（マイページから）');
      sessionStorage.removeItem('tsumugi_active_session');
      await fetchFirstMessage();
      return;
    }

    if (urlSessionId) {
      // URLにsessionIdがある場合 = 過去の会話の続き
      await loadSession(urlSessionId);
      return;
    }

    // ========================================
    // ハイブリッド復元ロジック
    // ========================================
    const RESUME_THRESHOLD_MS = 5 * 60 * 1000; // 5分
    const savedSession = sessionStorage.getItem('tsumugi_active_session');

    if (savedSession) {
      try {
        const { sessionId: savedSessionId, lastActivity, messages: savedMessages } = JSON.parse(savedSession);
        const elapsed = Date.now() - lastActivity;

        // 「続きから」ボタンから来た場合 → プロンプトなしで直接復元
        if (isContinueSession) {
          console.log('💬 続きから話すモード（マイページから）');
          setSessionId(savedSessionId);
          if (savedMessages && savedMessages.length > 0) {
            setMessages(savedMessages);
          } else {
            await loadSession(savedSessionId);
          }
          return;
        }

        if (elapsed < RESUME_THRESHOLD_MS) {
          // 5分以内 → 自動復元
          console.log(`✅ 5分以内の離脱 (${Math.round(elapsed / 1000)}秒) → セッション自動復元`);
          setSessionId(savedSessionId);
          if (savedMessages && savedMessages.length > 0) {
            setMessages(savedMessages);
          } else {
            await loadSession(savedSessionId);
          }
          return;
        } else {
          // 5分以上 → 「続きから話す？」プロンプトを表示
          console.log(`🕐 5分以上の離脱 (${Math.round(elapsed / 60000)}分) → 復元プロンプト表示`);
          setPendingResumeSessionId(savedSessionId);

          // キャンペーンモーダルが開いている場合は、閉じるまで待つ
          if (window.__campaignModalOpen) {
            console.log('⏳ キャンペーンモーダル表示中 → 閉じるまで待機');
            let alreadyShown = false;
            const checkInterval = setInterval(() => {
              if (!window.__campaignModalOpen && !alreadyShown) {
                alreadyShown = true;
                clearInterval(checkInterval);
                setShowResumePrompt(true);
                console.log('✅ キャンペーンモーダル閉じた → おかえりプロンプト表示');
              }
            }, 200);
            // 10秒経っても閉じなければ強制表示（フェイルセーフ）
            setTimeout(() => {
              clearInterval(checkInterval);
              if (!alreadyShown) {
                alreadyShown = true;
                setShowResumePrompt(true);
              }
            }, 10000);
          } else {
            setShowResumePrompt(true);
          }
          // 新規会話の初期メッセージは表示しつつ、プロンプトを出す
        }
      } catch (e) {
        console.error('セッション復元エラー:', e);
        sessionStorage.removeItem('tsumugi_active_session');
      }
    } else if (isContinueSession) {
      // 「続きから」を押したが、保存されたセッションがない場合 → 新規会話
      console.log('💬 続きから話すを選択したが、保存セッションなし → 新規会話');
      await fetchFirstMessage();
      return;
    }

    // sessionIdがない場合 = 新規会話
    // コアターゲット向けの初期挨拶を表示(使い方・履歴機能の案内含む)
    await fetchFirstMessage();
  };

  // セッション復元を実行
  const handleResumeSession = async () => {
    if (pendingResumeSessionId) {
      setShowResumePrompt(false);
      await loadSession(pendingResumeSessionId);
      setSessionId(pendingResumeSessionId);
      setPendingResumeSessionId(null);
    }
  };

  // 新規セッションで開始（復元しない）
  const handleStartNewSession = () => {
    setShowResumePrompt(false);
    setPendingResumeSessionId(null);
    sessionStorage.removeItem('tsumugi_active_session');
  };

  /**
   * 方向性選択ハンドラー（Mode 1用）
   * API呼び出しが必要な時点で表示される方向性選択
   * 選択後、保持していたユーザーメッセージ + intentType でAPI呼び出し
   */
  const handleDirectionChoice = async (type) => {
    console.log('🎯 方向性選択:', type);
    setDirectionChosen(true);
    setIntentType(type);
    setShowDirectionChoice(false);

    // 方向性プロンプトメッセージを削除
    setMessages(prev => prev.filter(msg => !msg.directionPrompt));

    // 保持していたユーザー入力 + intentType でAPI呼び出し
    if (pendingUserMessage) {
      await sendMessageToAPI(pendingUserMessage, type);
      setPendingUserMessage(null);
    }
  };

  /**
   * API呼び出し関数（方向性選択後に使用）
   * pendingUserMessage と intentType を受け取ってAPIを呼び出す
   */
  const sendMessageToAPI = async (messageText, selectedIntentType) => {
    console.log('📤 sendMessageToAPI called:', { messageText, selectedIntentType });

    setIsLoading(true);
    isLoadingRef.current = true;
    setIsTsumugiTyping(true);
    setDailyUsage(prev => prev + 1);

    try {
      // 会話履歴をAPI形式に変換（welcomeメッセージは除外）
      const conversationHistory = messages
        .filter(msg => !msg.welcome)
        .map(msg => ({
          role: msg.role === 'tsumugi' ? 'assistant' : 'user',
          content: msg.content
        }));

      const requestPayload = {
        userId,
        message: messageText,
        conversationHistory,
        userContext,
        sessionId,
        mode: 'chat',
        inputType: 'free_text',
        intentType: selectedIntentType
      };

      const token = localStorage.getItem('token');

      // 「準備中...」メッセージを先に追加
      const waitingMessageId = Date.now();
      setIsTsumugiTyping(false);
      setMessages(prev => [
        ...prev,
        {
          id: waitingMessageId,
          role: 'tsumugi',
          content: '準備中...',
          timestamp: new Date().toISOString(),
          isWaiting: true
        }
      ]);

      const response = await fetch(`${API_URL}/api/tsumugi/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify(requestPayload),
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'サーバーエラーが発生しました');
      }

      const data = await response.json();
      console.log('✅ API Response received:', { hasResponse: !!data.response });

      // APIから suggestedActions を取得
      const apiSuggestedActions = data.structuredResponse?.followUp?.suggestedActions || [];

      // 「準備中...」を実際の応答で置き換え
      setMessages(prev => prev.map(msg =>
        msg.id === waitingMessageId
          ? {
              ...msg,
              content: data.response,
              isWaiting: false,
              suggestedActions: apiSuggestedActions
            }
          : msg
      ));

      // セッションIDを更新
      if (data.sessionId && data.sessionId !== sessionId) {
        setSessionId(data.sessionId);
        sessionStorage.setItem('tsumugi_active_session', data.sessionId);
      }

    } catch (error) {
      console.error('❌ API Error:', error);
      // エラー時は準備中メッセージをエラーメッセージに置き換え
      setMessages(prev => prev.map(msg =>
        msg.isWaiting
          ? {
              ...msg,
              content: 'ごめんね、ちょっとうまく聞き取れなかった。もう一度話してくれる？',
              isWaiting: false
            }
          : msg
      ));
    } finally {
      setIsLoading(false);
      isLoadingRef.current = false;
      setIsTsumugiTyping(false);
    }
  };

  // 特定のセッションをロード
  const loadSession = async (sid) => {
    if (!userId) return;

    try {
      const response = await api.get(`/api/tsumugi/session/${sid}`, {
        params: { userId }
      });

      if (response.data.success) {
        const session = response.data.session;
        setSessionId(session.sessionId);

        // メッセージを復元
        const restoredMessages = session.messages.map(msg => ({
          role: msg.role,
          content: msg.content,
          timestamp: msg.timestamp
        }));

        setMessages(restoredMessages);

        // 方向性選択関連のstateをリセット（復元セッションでは新たに選択させる）
        setDirectionChosen(false);
        setShowDirectionChoice(false);
        setIntentType('default');
        setPendingUserMessage(null);
      }
    } catch (error) {
      console.error('Failed to load session:', error);
      alert('会話の読み込みに失敗しました');
      // エラー時は新規会話にリダイレクト
      navigate('/chat');
    }
  };

  const fetchFirstMessage = async () => {
    try {
      // オンボーディングからの場合は専用の初回メッセージを生成（疲労度ベース）
      if (isFromOnboarding && onboardingSituation) {
        const onboardingMessages = {
          // 疲労度ベースのメッセージ（当事者視点を前面に）
          moderate: 'まあまあ動けるんだね、よかった。\n\n調子いい日は貴重だもんね。私も同じだからわかるよ。\n\n今日はどうする？\nコンビニ行ける？それとも冷蔵庫にあるもので何とかする？',
          low: 'なんとか動けるんだね、えらいよ。\n\n私もそういう日ある。動こうとしてるだけで十分だよ。\n\nコンビニ行ける？それとも冷蔵庫見てみる？',
          very_low: '動けないよね、わかるよ。私もそういう日ある。\n\n今日は何もしなくていいよ。休むことが仕事だから。\n\n水だけ飲んで、あとは横になってて。明日また話そう。'
        };

        const message = onboardingMessages[onboardingSituation] || onboardingMessages.moderate;

        setMessages([
          {
            role: 'tsumugi',
            content: message,
            timestamp: new Date().toISOString()
          }
        ]);

        // オンボーディングsituationに基づいてモードを設定
        // 2025-12-02: 統合された新Mode（chat）に変更
        setSelectedMode('chat');

        // URLからクエリパラメータを削除（履歴を汚さない）
        navigate('/chat', { replace: true });
        return;
      }

      // 通常の初回メッセージ取得
      const situation = determineUserSituation(userContext);
      const response = await api.get('/api/tsumugi/first-message', {
        params: { userId, situation }
      });

      if (response.data.success) {
        setMessages([
          {
            role: 'tsumugi',
            content: response.data.message,
            timestamp: new Date().toISOString()
          }
        ]);
      }
    } catch (error) {
      console.error('Failed to fetch first message:', error);
      // フォールバック
      setMessages([
        {
          role: 'tsumugi',
          content: 'はじめまして、つむぎだよ。\n\n今日はどんな感じ？\n話したくなかったら、スルーでもいいよ。',
          timestamp: new Date().toISOString()
        }
      ]);
    }
  };

  const determineUserSituation = (context) => {
    if (!userId) return 'first_visit';
    if (context.lastVisit) {
      const daysSinceLastVisit = Math.floor(
        (Date.now() - new Date(context.lastVisit).getTime()) / (1000 * 60 * 60 * 24)
      );
      if (daysSinceLastVisit > 7) return 'long_absence';
    }
    if (context.badDayStreak >= 3) return 'bad_day_streak';
    if (context.visitCount > 1) return 'returning_user';
    return 'first_visit';
  };


  // 削除予定の会話を取得（Free会員のみ）
  const checkExpiringConversations = async () => {
    try {
      const response = await api.get('/api/tsumugi/expiring-conversations', {
        params: { userId, daysThreshold: 1 }
      });

      if (response.data.success && response.data.count > 0) {
        setExpiringConversations(response.data.conversations);
      }
    } catch (error) {
      console.error('Failed to check expiring conversations:', error);
    }
  };

  // プラン別の保存期間を取得
  const getRetentionPeriod = (plan) => {
    switch(plan.toLowerCase()) {
      case 'free':
        return '3日間';
      case 'standard':
        return '30日間';
      case 'premium':
      case 'premium_plus':
      case 'ultimate':
        return '永久保存';
      default:
        return '3日間';
    }
  };

  // プラン名を日本語に変換
  const getPlanName = (plan) => {
    switch(plan.toLowerCase()) {
      case 'free':
        return '無料';
      case 'standard':
        return 'スタンダード';
      case 'premium':
        return 'プレミアム';
      case 'premium_plus':
        return 'プレミアムプラス';
      case 'ultimate':
        return 'アルティメット';
      default:
        return plan;
    }
  };

  // セッション一覧を取得
  const loadSessions = async () => {
    if (!userId) return;

    try {
      const response = await api.get('/api/tsumugi/sessions', {
        params: { userId }
      });

      if (response.data.success) {
        setSessions(response.data.sessions);
      }
    } catch (error) {
      console.error('Failed to load sessions:', error);
    }
  };

  // セッションを選択して表示（URLをナビゲート）
  const selectSession = (session) => {
    // URLを /chat/:sessionId に変更して、そのセッションをロード
    navigate(`/chat/${session.sessionId}`);
  };

  // 日時をフォーマット
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return `今日 ${date.getHours()}:${String(date.getMinutes()).padStart(2, '0')}`;
    } else if (diffDays === 1) {
      return `昨日 ${date.getHours()}:${String(date.getMinutes()).padStart(2, '0')}`;
    } else {
      return `${date.getMonth() + 1}/${date.getDate()} ${date.getHours()}:${String(date.getMinutes()).padStart(2, '0')}`;
    }
  };

  // 削除までの日数を計算
  const getDaysUntilDeletion = (expiresAt) => {
    if (!expiresAt) return null;
    const now = new Date();
    const expiry = new Date(expiresAt);
    const diffDays = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const sendMessage = async (messageText = input, mode = selectedMode, inputType = 'free_text') => {
    console.log('=== sendMessage START ===', { messageText, mode, isLoading, isLoadingRef: isLoadingRef.current });
    console.log('🔍 sendMessage called with:', { messageText, mode, selectedMode, inputType });

    // 重複リクエスト防止: 同期的なRefでチェック（React stateは非同期なので不十分）
    if (isLoadingRef.current) {
      console.log('Already loading (ref check), ignoring duplicate request');
      return;
    }
    console.log('✅ isLoading check passed');

    if (!messageText.trim()) return;

    // 🎯 自由入力は全てMode 1（つむぎと話す）に統一
    // Mode 1は安定しており、会話履歴も渡すので文脈を理解した応答が返る
    const effectiveMode = inputType === 'free_text' ? 'chat' : mode;
    console.log('🎯 effectiveMode:', effectiveMode, '(inputType:', inputType, ', originalMode:', mode, ')');

    // 文字数チェック
    if (messageText.length > MAX_MESSAGE_LENGTH) {
      alert(`メッセージは${MAX_MESSAGE_LENGTH}文字以内でお願いします。\n\n現在: ${messageText.length}文字`);
      return;
    }

    // 利用回数チェック
    if (dailyUsage >= dailyLimit) {
      if (initialPlan === 'premium' || initialPlan === 'premium_plus') {
        alert(`つむぎより\n\n今日はたくさんお話しできて嬉しかったです！\nでも、つむぎもちょっと疲れちゃいました💭\n\n橋本病の改善には「適度な休息」も大切です。\n今日得たアドバイスをゆっくり振り返って、\n明日また元気にお話ししましょう！\n\n✨ 明日の0時にリフレッシュしてお待ちしています`);
      } else {
        alert(`本日の利用上限（${dailyLimit}回）に達しました。\n\n明日また来てね。\nもっと話したい場合は、有料プランをご検討ください。`);
      }
      return;
    }

    // ユーザーメッセージを追加
    const userMessage = {
      role: 'user',
      content: messageText,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    isLoadingRef.current = true; // 同期的にも更新（重複リクエスト防止のため）
    setIsTsumugiTyping(true);

    // ========================================
    // Mode 2 固定応答ロジック（API呼び出し削減）
    // ========================================
    if (mode === 'meal' && inputType === 'button') {
      // 固定応答マップから検索
      const fixedResponse = MODE2_FIXED_RESPONSES[messageText];

      if (fixedResponse) {
        console.log('🍳 Mode 2 固定応答を使用:', messageText);

        // 画面遷移の場合（予告メッセージを表示してからボタンで遷移）
        if (fixedResponse.navigateTo && fixedResponse.empathy) {
          const content = buildFixedResponse({
            empathy: fixedResponse.empathy,
            notes: fixedResponse.notes || ''
          });

          const tsumugiMessage = {
            role: 'tsumugi',
            content,
            timestamp: new Date().toISOString(),
            navigateTo: fixedResponse.navigateTo,
            navigateLabel: fixedResponse.navigateLabel || '移動する'
          };

          setTimeout(() => {
            setMessages(prev => [...prev, tsumugiMessage]);
            setIsLoading(false);
            isLoadingRef.current = false;
            setIsTsumugiTyping(false);
          }, 500);
          return;
        }

        // レシピ取得が必要な場合
        if (fixedResponse.fetchRecipes) {
          try {
            // レシピAPIを呼び出し
            const excludeIds = fixedResponse.excludePrevious ? suggestedRecipeIds : [];
            const response = await api.get('/api/tsumugi/meal/recipes', {
              params: {
                userId,
                fatigueLevel: fixedResponse.fatigueLevel || 'moderate',
                count: 3,
                exclude: excludeIds.join(',')
              }
            });

            if (response.data.success && response.data.recipes) {
              const recipes = response.data.recipes;
              // 提案済みIDを更新
              setSuggestedRecipeIds(prev => [...prev, ...recipes.map(r => r.id)]);

              const content = buildFixedResponse(fixedResponse, recipes);

              setTimeout(() => {
                setMessages(prev => [
                  ...prev,
                  {
                    role: 'tsumugi',
                    content: content,
                    timestamp: new Date().toISOString(),
                    fixedResponse: true,
                    suggestedActions: fixedResponse.followUp?.suggestedActions
                  }
                ]);
                setMode2State(messageText);
                setIsTsumugiTyping(false);
                setIsLoading(false);
                isLoadingRef.current = false;
              }, 300);
              return;
            }
          } catch (error) {
            console.error('レシピ取得エラー:', error);
            // エラー時はフォールバックとしてAPIを呼び出す
          }
        } else {
          // 固定応答のみ（API不要）
          const content = buildFixedResponse(fixedResponse);

          setTimeout(() => {
            setMessages(prev => [
              ...prev,
              {
                role: 'tsumugi',
                content: content,
                timestamp: new Date().toISOString(),
                fixedResponse: true,
                suggestedActions: fixedResponse.followUp?.suggestedActions
              }
            ]);
            setMode2State(messageText);
            setIsTsumugiTyping(false);
            setIsLoading(false);
            isLoadingRef.current = false;
          }, 300);

          // 利用回数をカウントしない（API呼び出しなし）
          return;
        }
      }
    }

    // Mode 2開始時（「食事の相談」ボタン）
    if ((messageText === '食事の相談' || messageText === '今日なにつくろう') && mode === 'meal') {
      console.log('🍳 Mode 2 開始（固定応答）');
      const startResponse = MODE2_FIXED_RESPONSES['mode2_start'];
      const content = buildFixedResponse(startResponse);

      setTimeout(() => {
        setMessages(prev => [
          ...prev,
          {
            role: 'tsumugi',
            content: content,
            timestamp: new Date().toISOString(),
            fixedResponse: true,
            suggestedActions: startResponse.followUp?.suggestedActions
          }
        ]);
        setMode2State('mode2_start');
        setIsTsumugiTyping(false);
        setIsLoading(false);
        isLoadingRef.current = false;
      }, 300);
      return;
    }

    // ========================================
    // Mode 3 固定応答ロジック（症状ヒアリング → FormPageV2誘導）
    // ========================================
    if (mode === 'symptom' && inputType === 'button') {
      // 固定応答マップから検索
      const fixedResponse = MODE3_FIXED_RESPONSES[messageText];

      if (fixedResponse) {
        console.log('💊 Mode 3 固定応答を使用:', messageText);

        // 画面遷移の場合（予告メッセージを表示してからボタンで遷移）
        if (fixedResponse.navigateTo && fixedResponse.empathy) {
          const navContent = buildFixedResponse({
            empathy: fixedResponse.empathy,
            notes: fixedResponse.notes || ''
          });

          // 症状プリセットをURLパラメータで渡す
          const presetParam = lastSymptomPreset ? `?preset=${lastSymptomPreset}` : '';

          const tsumugiMessage = {
            role: 'tsumugi',
            content: navContent,
            timestamp: new Date().toISOString(),
            navigateTo: fixedResponse.navigateTo + presetParam,
            navigateLabel: fixedResponse.navigateLabel || '移動する'
          };

          setTimeout(() => {
            setMessages(prev => [...prev, tsumugiMessage]);
            setIsLoading(false);
            isLoadingRef.current = false;
            setIsTsumugiTyping(false);
          }, 500);
          return;
        }

        // 症状プリセットを保存（次の「詳しく相談する」で使用）
        if (fixedResponse.symptomPreset) {
          setLastSymptomPreset(fixedResponse.symptomPreset);
        }

        // 固定応答を表示
        const content = buildFixedResponse(fixedResponse);

        setTimeout(() => {
          setMessages(prev => [
            ...prev,
            {
              role: 'tsumugi',
              content: content,
              timestamp: new Date().toISOString(),
              fixedResponse: true,
              suggestedActions: fixedResponse.followUp?.suggestedActions,
              isEnding: fixedResponse.isEnding || false
            }
          ]);
          setMode3State(messageText);
          setIsTsumugiTyping(false);
          setIsLoading(false);
          isLoadingRef.current = false;
        }, 300);

        // 利用回数をカウントしない（API呼び出しなし）
        return;
      }
    }

    // Mode 3開始時（「体調の相談」ボタン）
    if (messageText === '体調の相談' && mode === 'symptom') {
      console.log('💊 Mode 3 開始（固定応答）');
      const startResponse = MODE3_FIXED_RESPONSES['mode3_start'];
      const content = buildFixedResponse(startResponse);

      setTimeout(() => {
        setMessages(prev => [
          ...prev,
          {
            role: 'tsumugi',
            content: content,
            timestamp: new Date().toISOString(),
            fixedResponse: true,
            suggestedActions: startResponse.followUp?.suggestedActions
          }
        ]);
        setMode3State('mode3_start');
        setLastSymptomPreset(null); // プリセットをリセット
        setIsTsumugiTyping(false);
        setIsLoading(false);
        isLoadingRef.current = false;
      }, 300);
      return;
    }

    // ========================================
    // Mode 1 固定応答ロジック（API呼び出し削減）
    // ボタンクリック時のみ固定応答を使用
    // ========================================
    if (mode === 'chat' && inputType === 'button') {
      const fixedResponse = MODE1_FIXED_RESPONSES[messageText];

      if (fixedResponse) {
        console.log('💬 Mode 1 固定応答を使用:', messageText);

        // promptFreeInput: 自由入力を促す場合は応答後に入力待ちにする
        const content = buildFixedResponse(fixedResponse);

        setTimeout(() => {
          setMessages(prev => [
            ...prev,
            {
              role: 'tsumugi',
              content: content,
              timestamp: new Date().toISOString(),
              fixedResponse: true,
              suggestedActions: fixedResponse.followUp?.suggestedActions,
              promptFreeInput: fixedResponse.promptFreeInput || false,
              isEnding: fixedResponse.isEnding || false
            }
          ]);
          setIsTsumugiTyping(false);
          setIsLoading(false);
          isLoadingRef.current = false;
        }, 300);

        // 利用回数をカウントしない（API呼び出しなし）
        return;
      }
    }

    // ========================================
    // Mode 1: API呼び出しが必要な場合の処理
    // 方向性未選択 かつ 自由入力の場合のみ 3択を表示してAPI呼び出しを保留
    // ボタンクリック（初回カード等）では方向性選択をスキップ
    // ========================================
    if (effectiveMode === 'chat' && !directionChosen && inputType === 'free_text') {
      console.log('🎯 方向性未選択 - 3択を表示してAPI呼び出しを保留');

      // ユーザーメッセージを保持
      setPendingUserMessage(messageText);

      // 方向性選択UIを表示するためのメッセージを追加
      setTimeout(() => {
        setMessages(prev => [
          ...prev,
          {
            role: 'tsumugi',
            content: 'そっか。\n何か一緒に考えたいこととかある？',
            timestamp: new Date().toISOString(),
            directionPrompt: true // 方向性選択プロンプトフラグ
          }
        ]);
        setShowDirectionChoice(true);
        setIsTsumugiTyping(false);
        setIsLoading(false);
        isLoadingRef.current = false;
      }, 300);

      return;
    }

    // ========================================
    // ストリーミングAPI呼び出し（リアルタイム表示）
    // ========================================

    setDailyUsage(prev => prev + 1); // 利用回数カウント

    try {
      // 会話履歴をAPI形式に変換（welcomeメッセージは除外）
      const conversationHistory = messages
        .filter(msg => !msg.welcome) // 「おかえり」メッセージを除外
        .map(msg => ({
          role: msg.role === 'tsumugi' ? 'assistant' : 'user',
          content: msg.content
        }));

      console.log(`📤 API送信（ストリーミング） - mode: ${mode}, message: ${messageText.substring(0, 20)}...`);

      const requestPayload = {
        userId,
        message: messageText,
        conversationHistory,
        userContext,
        sessionId, // 現在のセッションIDを送信
        mode: effectiveMode, // 自由入力時は 'chat' (Mode 1) に統一
        inputType, // 'button' or 'free_text' - Phase 5: 自由入力検出用
        intentType // Mode 1: 方向性選択（venting, information-seeking, silent, default）
      };

      // トークンを取得
      const token = localStorage.getItem('token');

      // 「準備中...」メッセージを先に追加
      const waitingMessageId = Date.now();
      setIsTsumugiTyping(false);
      setMessages(prev => [
        ...prev,
        {
          id: waitingMessageId,
          role: 'tsumugi',
          content: '準備中...',
          timestamp: new Date().toISOString(),
          isWaiting: true
        }
      ]);

      // 前のリクエストをキャンセル（新しいリクエスト開始時）
      const previousController = abortControllerRef.current;
      abortControllerRef.current = new AbortController();
      if (previousController) {
        console.log('🔄 Previous request aborted');
        previousController.abort();
      }

      console.log('🚀 Starting fetch request to:', `${API_URL}/api/tsumugi/chat`);

      // 非ストリーミングAPIを呼び出し
      const response = await fetch(`${API_URL}/api/tsumugi/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify(requestPayload),
        signal: abortControllerRef.current.signal,
        credentials: 'include'
      });

      // レスポンスヘッダーをログ出力
      console.log('📥 fetch response received', {
        ok: response.ok,
        status: response.status,
        contentType: response.headers.get('content-type')
      });

      // HTTPステータスチェック
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.error || 'サーバーエラーが発生しました');
        error.response = { status: response.status, data: errorData };
        throw error;
      }

      // JSONレスポンスを取得
      const data = await response.json();
      console.log('✅ Response received:', {
        hasResponse: !!data.response,
        sessionId: data.sessionId,
        hasUsage: !!data.usage
      });

      // サーバーからのレート情報を反映
      if (data.usage) {
        setDailyUsage(data.usage.dailyUsage || dailyUsage);
        setLastApiCallInfo(data.usage);
      }

      // セッションIDを更新
      if (data.sessionId) {
        setSessionId(data.sessionId);
      }

      // Progressive Disclosure: 最後の1回を使い切った場合
      if (data.usage?.isLastCall) {
        setTimeout(() => setShowLimitModal(true), 1500);
      }

      // 最終メッセージで更新
      let tsumugiContent = data.response;
      if (data.usage?.isNearLimit && data.usage?.wasApiCall) {
        tsumugiContent += '\n\n---\n💭 *今日はあと1回、ゆっくり話せるよ*';
      }

      const apiSuggestedActions = data.structuredResponse?.followUp?.suggestedActions || [];

      // 「準備中...」メッセージをつむぎの応答に置換
      setMessages(prev => prev.map(msg =>
        msg.id === waitingMessageId
          ? {
              ...msg,
              content: tsumugiContent,
              isWaiting: false,
              fallback: data.fallback,
              navigateTo: data.navigateTo,
              suggestedActions: apiSuggestedActions
            }
          : msg
      ));

      setIsTsumugiTyping(false);
      setIsLoading(false);
      isLoadingRef.current = false;
    } catch (error) {
      console.log('❌ Error caught:', error.name, error.message);
      // AbortError（リクエストキャンセル）は正常終了扱い
      if (error.name === 'AbortError') {
        console.log('Request was aborted');
        // 待機中メッセージを削除
        setMessages(prev => prev.filter(msg => !msg.isWaiting));
        setIsTsumugiTyping(false);
        setIsLoading(false);
        isLoadingRef.current = false;
        return;
      }

      console.error('Chat error:', error);

      // バックエンドからのエラーメッセージを取得
      const errorMessage = error.response?.data?.error || error.message || '';
      const statusCode = error.response?.status;

      // 認証エラーの場合
      if (statusCode === 401) {
        localStorage.removeItem('token');
        setTimeout(() => {
          // 待機中メッセージを更新
          setMessages(prev => prev.map(msg =>
            msg.isWaiting
              ? {
                  ...msg,
                  content: 'ログインが切れちゃったみたい。\nもう一度ログインしてね。',
                  isWaiting: false,
                  error: true
                }
              : msg
          ));
          setIsTsumugiTyping(false);
        }, 300);
        return;
      }

      // レート制限の場合
      if (statusCode === 429) {
        // 待機中メッセージを削除（モーダルを表示するので）
        setMessages(prev => prev.filter(msg => !msg.isWaiting));
        setShowLimitModal(true);
        setIsTsumugiTyping(false);
        return;
      }

      // ポイント不足の場合（402 INSUFFICIENT_POINTS）
      const errorData = error.response?.data;
      if (statusCode === 402 || errorData?.errorCode === 'INSUFFICIENT_POINTS') {
        setMessages(prev => prev.filter(msg => !msg.isWaiting));
        setPointsExhaustedInfo({
          remainingPoints: errorData?.remainingPoints ?? 0,
          requiredPoints: errorData?.requiredPoints ?? 1,
          monthlyPoints: errorData?.monthlyPoints ?? null
        });
        setShowPointsExhaustedModal(true);
        setIsTsumugiTyping(false);
        setIsLoading(false);
        isLoadingRef.current = false;
        return;
      }

      // ネットワークエラーの場合
      if (!navigator.onLine || error.message === 'Failed to fetch') {
        setTimeout(() => {
          // 待機中メッセージを更新
          setMessages(prev => prev.map(msg =>
            msg.isWaiting
              ? {
                  ...msg,
                  content: 'ネットワークに繋がらないみたい。\n接続を確認してね。',
                  isWaiting: false,
                  error: true
                }
              : msg
          ));
          setIsTsumugiTyping(false);
        }, 300);
        return;
      }

      // WELLNESS_BREAKメッセージの場合は特別処理
      if (errorMessage.includes('WELLNESS_BREAK:')) {
        const wellnessMessage = errorMessage.replace('WELLNESS_BREAK:', '');
        setTimeout(() => {
          // 待機中メッセージを更新または追加
          setMessages(prev => {
            const hasWaitingMessage = prev.some(msg => msg.isWaiting);
            if (hasWaitingMessage) {
              return prev.map(msg =>
                msg.isWaiting
                  ? {
                      ...msg,
                      content: wellnessMessage,
                      isWaiting: false,
                      wellness: true
                    }
                  : msg
              );
            } else {
              return [
                ...prev,
                {
                  role: 'tsumugi',
                  content: wellnessMessage,
                  timestamp: new Date().toISOString(),
                  wellness: true
                }
              ];
            }
          });
          setIsTsumugiTyping(false);
        }, 300);
      } else {
        // 通常のエラーフォールバック
        setTimeout(() => {
          // 待機中のメッセージがあれば更新、なければ追加
          setMessages(prev => {
            const hasWaitingMessage = prev.some(msg => msg.isWaiting);
            if (hasWaitingMessage) {
              // 待機中メッセージを更新
              return prev.map(msg =>
                msg.isWaiting
                  ? {
                      ...msg,
                      content: 'ごめんね、ちょっと調子が悪いみたい。\n\nでも、聞いてるよ。\nまた話してね。',
                      isWaiting: false,
                      error: true
                    }
                  : msg
              );
            } else {
              // 新しいエラーメッセージを追加
              return [
                ...prev,
                {
                  role: 'tsumugi',
                  content: 'ごめんね、ちょっと調子が悪いみたい。\n\nでも、聞いてるよ。\nまた話してね。',
                  timestamp: new Date().toISOString(),
                  error: true
                }
              ];
            }
          });
          setIsTsumugiTyping(false);
        }, 300);
      }
    } finally {
      console.log('=== sendMessage END ===');
      setIsLoading(false);
      isLoadingRef.current = false;
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // 音声入力の初期化と処理
  const startVoiceInput = () => {
    // Web Speech API対応チェック
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      alert('お使いのブラウザは音声入力に対応していません。\n\nChrome、Edge、Safariをお使いください。');
      return;
    }

    if (isRecording) {
      // 録音停止
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      setIsRecording(false);
      return;
    }

    // 音声認識の開始
    const recognition = new SpeechRecognition();
    recognition.lang = 'ja-JP';
    recognition.continuous = false;
    recognition.interimResults = false;

    recognition.onstart = () => {
      setIsRecording(true);
    };

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setInput(prev => prev + transcript);
      setIsRecording(false);
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      setIsRecording(false);

      if (event.error === 'no-speech') {
        alert('音声が認識できませんでした。\nもう一度お試しください。');
      } else if (event.error === 'not-allowed') {
        alert('マイクへのアクセスが許可されていません。\nブラウザの設定でマイクを許可してください。');
      }
    };

    recognition.onend = () => {
      setIsRecording(false);
    };

    recognitionRef.current = recognition;
    recognition.start();
  };

  // +ボタンメニューの切り替え
  const toggleAttachMenu = () => {
    setShowAttachMenu(prev => !prev);
  };

  // ハンバーガーメニューの切り替え
  const toggleMenu = () => {
    setShowMenu(prev => !prev);
  };

  // 新しい会話を始める
  const startNewConversation = async () => {
    try {
      if (userId && sessionId) {
        // 現在のセッションを終了
        await api.post('/api/tsumugi/session/close', {
          userId,
          sessionId
        });
      }

      // セッション一覧を更新
      if (userId) {
        await loadSessions();
      }

      // ハイブリッド復元用のセッション情報をクリア（5分以内の自動復元を防ぐ）
      sessionStorage.removeItem('tsumugi_active_session');

      // 状態をリセット
      setSessionId(null);
      setMessages([]);
      setShowMenu(false); // メニューを閉じる
      setActiveTab('chat'); // チャットタブに切り替え

      // 方向性選択関連のstateをリセット
      setDirectionChosen(false);
      setShowDirectionChoice(false);
      setIntentType('default');
      setPendingUserMessage(null);

      // モード関連もリセット
      setSelectedMode(null);

      // 新規の初期メッセージを取得
      await fetchFirstMessage();

      // URLが /chat/:sessionId の場合は /chat にリダイレクト
      if (urlSessionId) {
        navigate('/chat', { replace: true });
      }

    } catch (error) {
      console.error('Failed to start new conversation:', error);
      alert('新しい会話の開始に失敗しました');
    }
  };

  // 新規ユーザーウェルカム完了ハンドラー
  const handleWelcomeComplete = (situation) => {
    setWelcomeCompleted(true);
    setIsNewUser(false);

    // onboardingSituationのロジックを利用して初回メッセージを設定
    const onboardingMessages = {
      // 疲労度ベースのメッセージ（当事者視点を前面に）
      moderate: 'まあまあ動けるんだね、よかった。\n\n調子いい日は貴重だもんね。私も同じだからわかるよ。\n\n今日はどうする？',
      low: 'なんとか動けるんだね、えらいよ。\n\n私もそういう日ある。動こうとしてるだけで十分だよ。\n\n何かあった？',
      very_low: '動けないよね、わかるよ。私もそういう日ある。\n\n今日は何もしなくていいよ。休むことが仕事だから。\n\n無理しないでね。'
    };

    const message = onboardingMessages[situation] || onboardingMessages.moderate;

    setMessages([
      {
        role: 'tsumugi',
        content: message,
        timestamp: new Date().toISOString()
      }
    ]);

    // URLからnewUserパラメータを削除
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('newUser');
    const newUrl = newSearchParams.toString()
      ? `${window.location.pathname}?${newSearchParams.toString()}`
      : window.location.pathname;
    window.history.replaceState({}, '', newUrl);
  };

  // マイページへ移動
  const goToMyPage = () => {
    window.location.href = '/mypage';
  };

  // モード選択ハンドラー
  const handleModeSelect = (modeId) => {
    setSelectedMode(modeId);
    setShowModeSelector(false);
    console.log(`✅ モード選択: ${modeId}`);
  };

  // モード切替ボタンを表示
  const toggleModeSelector = () => {
    setShowModeSelector(true);
  };

  // クイック返信ボタン（会話の流れで動的に変化）
  const getQuickReplies = () => {
    const turnCount = messages.length;

    // 初回（0-1ターン）: 3つのモードに対応する選択肢
    // 2025-12-02: 4モード→3モードに統合
    // - chat: つむぎと話す（旧quick + 旧talk を統合）
    // - meal: 食事の相談
    // - symptom: 体調の相談
    if (turnCount <= 1) {
      return [
        { text: 'つむぎと話したい', emoji: '💬', mode: 'chat' },
        { text: '食事の相談', emoji: '🍳', mode: 'meal' },
        { text: '体調の相談', emoji: '💊', mode: 'symptom' }
      ];
    }

    // 2-3ターン: 詳細選択
    if (turnCount <= 3) {
      return [
        { text: '仕事で疲れた', emoji: '💼', category: 'work' },
        { text: '体がだるい', emoji: '🤒', category: 'physical' },
        { text: 'もっと話したい', emoji: '💭', category: 'continue' },
        { text: '気分が沈んでる', emoji: '🌧️', category: 'mental' }
      ];
    }

    // 4ターン以上: アクション選択
    return [
      { text: '少し楽になった', emoji: '😌' },
      { text: 'もっと話したい', emoji: '💬' },
      { text: 'アドバイスがほしい', emoji: '💡' },
      { text: '今日はここまで', emoji: '👋' }
    ];
  };

  const quickReplies = getQuickReplies();

  return (
    <div className="tsumugi-chat-fullscreen">
      {/* レシピ生成中のローディング画面 */}
      {isGeneratingRecipe && (
        <div className="tsumugi-loading-overlay">
          <div className="tsumugi-loading-content">
            <div className="tsumugi-message tsumugi typing">
              <div className="message-avatar">
                <img src="/tsumugi.png" alt="つむぎ" />
              </div>
              <div className="message-bubble">
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
            <p className="tsumugi-loading-text">レシピを作っているよ...</p>
          </div>
        </div>
      )}

      {/* ヘッダー（LINE風） */}
      <div className="tsumugi-chat-header">
        <button className="header-btn back-btn" onClick={() => navigate('/mypage')} aria-label="戻る">
          ←
        </button>
        <div className="header-center">
          <div className="tsumugi-avatar-small">
            <img src="/tsumugi2.png" alt="つむぎ" />
          </div>
          <span className="tsumugi-name">つむぎ</span>
        </div>
        <div className="header-actions">
          <button className="header-btn menu-btn" onClick={toggleMenu} aria-label="メニュー">
            ☰
          </button>
        </div>
      </div>

      {/* タブナビゲーション */}
      {userId && (
        <div className="tsumugi-tabs">
          <button
            className={`tsumugi-tab ${activeTab === 'chat' ? 'active' : ''}`}
            onClick={() => setActiveTab('chat')}
          >
            <i className="fas fa-comment"></i>
            <span>会話</span>
          </button>
          <button
            className={`tsumugi-tab ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            <i className="fas fa-history"></i>
            <span>履歴</span>
            {sessions.length > 0 && (
              <span className="tab-badge">{sessions.length}</span>
            )}
          </button>
        </div>
      )}

      {/* ハンバーガーメニュー */}
      <HamburgerMenu
        isOpen={showMenu}
        onClose={toggleMenu}
        menuItems={[
          { icon: 'fas fa-plus-circle', label: '新しい会話を始める', onClick: startNewConversation },
          { icon: 'fas fa-book-open', label: '保存したレシピ', onClick: () => { navigate('/recipes'); toggleMenu(); } },
          { icon: 'fas fa-clipboard-list', label: '栄養相談フォーム', onClick: () => { navigate('/form-v2'); toggleMenu(); } },
          { icon: 'fas fa-user', label: 'マイページへ', onClick: goToMyPage },
          { icon: 'fas fa-question-circle', label: '使い方・ヘルプ', onClick: () => navigate('/how-to-use') },
        ]}
      />

      {/* プラン情報と警告バナー */}
      {userId && activeTab === 'chat' && (
        <div className="tsumugi-plan-info">
          <div className="plan-info-row">
            <div className="plan-badge">
              {getPlanName(userPlan)}プラン - 会話履歴: {getRetentionPeriod(userPlan)}
            </div>
            <button className="new-chat-btn-inline" onClick={startNewConversation} title="新しい会話">
              <i className="fas fa-plus"></i>
            </button>
          </div>
          {userPlan.toLowerCase() === 'free' && sessions.length > 0 && (
            <div className="free-plan-warning">
              <div className="warning-content">
                <i className="fas fa-info-circle"></i>
                <div className="warning-text">
                  <span className="warning-title">無料プランは3日で会話が消えます</span>
                  <span className="warning-subtitle">大切な会話を残したい方へ</span>
                </div>
              </div>
              <button
                className="upgrade-cta-btn"
                onClick={() => navigate('/plans')}
              >
                プラン変更
              </button>
            </div>
          )}
          {expiringConversations.length > 0 && userPlan.toLowerCase() === 'free' && (
            <div className="expiring-warning">
              <i className="fas fa-exclamation-triangle"></i>
              <span>{expiringConversations.length}件の会話があと1日で削除されます</span>
              <button
                className="upgrade-btn"
                onClick={() => navigate('/plans')}
              >
                今すぐ保存
              </button>
            </div>
          )}
        </div>
      )}

      {/* 履歴タブのコンテンツ */}
      {activeTab === 'history' && userId && (
        <div className="tsumugi-history-container">
          <div className="history-header">
            <h3 className="history-title">
              <i className="fas fa-history"></i>
              過去の会話
            </h3>
            <p className="history-subtitle">
              {userPlan.toLowerCase() === 'free' ? '過去3日間の会話' :
               userPlan.toLowerCase() === 'standard' ? '過去30日間の会話' :
               '全ての会話'}
            </p>
          </div>

          {sessions.length === 0 ? (
            <div className="history-empty">
              <i className="fas fa-comment-slash"></i>
              <p>まだ会話履歴がありません</p>
              <button
                className="start-chat-btn"
                onClick={() => setActiveTab('chat')}
              >
                新しい会話を始める
              </button>
            </div>
          ) : (
            <div className="history-list">
              {sessions.map((session) => {
                const firstUserMessage = session.messages?.find(m => m.role === 'user');
                const firstTsumugiMessage = session.messages?.find(m => m.role === 'assistant' || m.role === 'tsumugi');
                const daysUntilDeletion = getDaysUntilDeletion(session.expiresAt);

                // 会話のタイトルを生成（最初のユーザーメッセージから）
                const conversationTitle = firstUserMessage
                  ? firstUserMessage.content.slice(0, 50) + (firstUserMessage.content.length > 50 ? '...' : '')
                  : '会話を開始';

                // つむぎの返答のプレビュー
                const tsumugiPreview = firstTsumugiMessage
                  ? firstTsumugiMessage.content.slice(0, 40) + (firstTsumugiMessage.content.length > 40 ? '...' : '')
                  : '';

                return (
                  <div
                    key={session.sessionId}
                    className="history-item"
                    onClick={() => selectSession(session)}
                  >
                    <div className="history-item-header">
                      <span className="history-date">
                        <i className="fas fa-calendar"></i>
                        {formatDate(session.createdAt)}
                      </span>
                      {session.status === 'active' && (
                        <span className="history-badge active">進行中</span>
                      )}
                    </div>
                    <div className="history-item-content">
                      <p className="history-title-text">
                        💬 {conversationTitle}
                      </p>
                      {tsumugiPreview && (
                        <p className="history-tsumugi-preview">
                          <img src="/tsumugi.png" alt="つむぎ" />
                          {tsumugiPreview}
                        </p>
                      )}
                      <p className="history-meta">
                        <i className="fas fa-comment-dots"></i>
                        {session.messageCount || session.messages?.length || 0}件のやりとり
                        {session.context?.mood && (
                          <span className="history-mood"> • {session.context.mood === 'low' ? '😔' : session.context.mood === 'medium' ? '😌' : '😊'}</span>
                        )}
                      </p>
                    </div>
                    {daysUntilDeletion !== null && daysUntilDeletion <= 3 && (
                      <div className="history-warning">
                        <i className="fas fa-exclamation-triangle"></i>
                        あと{daysUntilDeletion}日で削除されます
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* 新規ユーザーウェルカム画面 */}
      {activeTab === 'chat' && isNewUser && !welcomeCompleted && (
        <div className="tsumugi-welcome-overlay">
          <div className="tsumugi-welcome-content">
            <img src="/tsumugi.png" alt="つむぎ" className="tsumugi-welcome-mascot" />
            <h2 className="tsumugi-welcome-title">つむぎです。</h2>
            <p className="tsumugi-welcome-message">
              疲れてても大丈夫。<br />
              私も橋本病だから、わかるよ。
            </p>
            <p className="tsumugi-welcome-prompt">今日はどんな感じ？</p>
            <div className="tsumugi-welcome-choices">
              <button onClick={() => handleWelcomeComplete('moderate')}>
                <span>&#128578;</span> まあまあ動ける
              </button>
              <button onClick={() => handleWelcomeComplete('low')}>
                <span>&#128528;</span> なんとか動ける
              </button>
              <button onClick={() => handleWelcomeComplete('very_low')}>
                <span>&#128532;</span> 動けない
              </button>
            </div>
          </div>
        </div>
      )}

      {/* メッセージエリア */}
      {activeTab === 'chat' && (
      <div className="tsumugi-messages">
        {messages.map((msg, index) => (
          <React.Fragment key={index}>
            <div
              className={`tsumugi-message ${msg.role === 'tsumugi' ? 'tsumugi' : 'user'}`}
            >
              {msg.role === 'tsumugi' && (
                <div className="message-avatar">
                  <img src="/tsumugi.png" alt="つむぎ" />
                </div>
              )}
              <div className="message-bubble">
                {msg.role === 'tsumugi' ? (
                  <div className={`message-content markdown-content${msg.isWaiting ? ' streaming-waiting' : ''}`}>
                    {(() => {
                      const recipePattern = /\[レシピを見る:\s*([^\]]+)\]/g;
                      const recipes = [];
                      let match;
                      while ((match = recipePattern.exec(msg.content)) !== null) {
                        recipes.push(match[1].trim());
                      }
                      const contentWithoutRecipeButtons = msg.content.replace(/\[レシピを見る:\s*[^\]]+\]/g, '').trim();
                      return (
                        <>
                          <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                            {contentWithoutRecipeButtons}
                          </ReactMarkdown>
                          {recipes.length > 0 && (
                            <div className="tsumugi-recipe-buttons">
                              {recipes.map((recipeName, idx) => (
                                <button key={`recipe-${idx}`} className="tsumugi-recipe-button" onClick={() => handleRecipeButtonClick(recipeName)}>
                                  <span className="recipe-icon">🍳</span>
                                  レシピを見る: {recipeName}
                                </button>
                              ))}
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                ) : (
                  <p className="message-content">{msg.content}</p>
                )}
                {msg.fallback && (
                  <p className="message-note tsumugi-caption">
                    ※簡易応答モード
                  </p>
                )}
                {/* 画面遷移ボタン */}
                {msg.navigateTo && (
                  <button
                    className="tsumugi-navigate-button"
                    onClick={() => navigate(msg.navigateTo)}
                  >
                    {msg.navigateLabel || (
                      <>
                        {msg.navigateTo === '/fridge-photo' && '📷 冷蔵庫を撮影する'}
                        {msg.navigateTo === '/fridge-recipe' && '🔍 食材を選ぶ'}
                        {msg.navigateTo === '/flyer-recipe' && '🛒 特売品を撮影する'}
                        {msg.navigateTo === '/ingredient-check' && '🔬 成分表をチェックする'}
                        {msg.navigateTo.startsWith('/form-v2') && '💊 相談画面へ'}
                      </>
                    )}
                  </button>
                )}
              </div>
              {msg.role === 'user' && (
                <div className="message-avatar user">
                  <span className="emoji">👤</span>
                </div>
              )}
            </div>

            {/* 広告: 10メッセージ（5往復）ごとに表示（Premiumプランは除く） */}
            {(index + 1) % 10 === 0 && index > 0 && initialPlan !== 'premium' && initialPlan !== 'premium_plus' && (
              <div className="tsumugi-ad-wrapper">
                <AdBanner />
              </div>
            )}
          </React.Fragment>
        ))}

        {/* タイピングインジケーター */}
        {isTsumugiTyping && (
          <div className="tsumugi-message tsumugi typing">
            <div className="message-avatar">
              <img src="/tsumugi.png" alt="つむぎ" />
            </div>
            <div className="message-bubble">
              <div className="typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        )}

        {/* LINE風クイックリプライ（横スクロール + フェード + ドット） */}
        {dailyUsage < dailyLimit && !isTsumugiTyping && (() => {
          // スクロール位置に基づいてアクティブなドットを計算
          const handleQuickReplyScroll = (e) => {
            const target = e.target;
            const scrollLeft = target.scrollLeft;
            const scrollWidth = target.scrollWidth - target.clientWidth;

            // スクロールが少しでもあればフェードを解除
            if (scrollLeft > 10) {
              target.classList.add('scrolled');
            } else {
              target.classList.remove('scrolled');
            }

            // ドットのアクティブ状態を更新
            const dots = target.parentElement.querySelectorAll('.line-quick-replies-dot');
            const progress = scrollWidth > 0 ? scrollLeft / scrollWidth : 0;
            const activeIndex = Math.round(progress * (dots.length - 1));

            dots.forEach((dot, i) => {
              if (i === activeIndex) {
                dot.classList.add('active');
              } else {
                dot.classList.remove('active');
              }
            });
          };

          // 初回（0-1ターン）は特別なカードデザイン
          const isInitialState = messages.length <= 1;

          if (isInitialState) {
            const modeCards = [
              {
                mode: 'chat',
                emoji: '💬',
                title: 'つむぎと話したい',
                desc: '聞いてほしい・相談したい',
                color: 'mint'
              },
              {
                mode: 'meal',
                emoji: '🍳',
                title: '食事の相談',
                desc: '何を食べればいいか知りたい',
                color: 'peach'
              },
              {
                mode: 'symptom',
                emoji: '💊',
                title: '体調の相談',
                desc: '症状について聞きたい',
                color: 'lavender'
              }
            ];

            return (
              <>
              <div className="tsumugi-chat__guide-hint">
                <span>下のボタンから気になることを選んでね</span>
                <Link to="/how-to-use" className="tsumugi-chat__guide-link">
                  はじめてのかたへ ── つむぎの使い方
                </Link>
              </div>
              <div className="initial-mode-cards-container">
                {modeCards.map((card, index) => (
                  <button
                    key={index}
                    className={`initial-mode-card initial-mode-card--${card.color}`}
                    onClick={() => {
                      console.log('🔍 Initial mode card clicked:', card.mode);
                      handleModeSelect(card.mode);
                      sendMessage(card.title, card.mode, 'button');
                    }}
                    disabled={isLoading || dailyUsage >= dailyLimit}
                  >
                    <span className="initial-mode-card__emoji">{card.emoji}</span>
                    <span className="initial-mode-card__title">{card.title}</span>
                    <span className="initial-mode-card__desc">{card.desc}</span>
                  </button>
                ))}
              </div>
              </>
            );
          }

          // suggestedActionsがある場合はここでは表示しない
          // （下の固定応答セクションで表示される）
          const lastTsumugiMessage = [...messages].reverse().find(m => m.role === 'tsumugi');
          if (lastTsumugiMessage?.suggestedActions?.length > 0) {
            return null;
          }

          // Mode 1（つむぎと話したい）は自由入力メイン
          // 大きいクイックリプライは表示しない（既存の mode1-suggestions-wrapper を使用）
          if (selectedMode === 'chat') {
            return null;
          }

          // Mode 2, 3 は従来通りの大きいボタン
          return (
            <div className="tsumugi-line-quick-replies-container">
              <div
                className="tsumugi-line-quick-replies-wrapper"
                onScroll={handleQuickReplyScroll}
              >
                <div className="tsumugi-line-quick-replies">
                  {quickReplies.map((reply, index) => (
                    <button
                      key={index}
                      className="line-quick-reply-btn"
                      onClick={() => {
                        console.log('🔍 Quick reply clicked:', { text: reply.text, mode: reply.mode });
                        if (reply.mode) {
                          handleModeSelect(reply.mode);
                          sendMessage(reply.text, reply.mode, 'button');
                        } else {
                          sendMessage(reply.text, selectedMode, 'button');
                        }
                      }}
                      disabled={isLoading || dailyUsage >= dailyLimit}
                    >
                      <span className="line-qr-emoji">{reply.emoji}</span>
                      <span className="line-qr-text">{reply.text}</span>
                    </button>
                  ))}
                </div>
              </div>
              {/* ドットインジケーター */}
              <div className="line-quick-replies-dots">
                {quickReplies.map((_, index) => (
                  <span
                    key={index}
                    className={`line-quick-replies-dot ${index === 0 ? 'active' : ''}`}
                  />
                ))}
              </div>
            </div>
          );
        })()}

        <div ref={messagesEndRef} />
      </div>
      )}

      {/* Mode 1: 方向性選択モーダル（API呼び出しが必要な時点で表示） */}
      {/* 注意: 自由入力は全てMode 1として処理されるため、selectedModeに関係なく表示 */}
      {activeTab === 'chat' && showDirectionChoice && !directionChosen && (
        <div className="direction-modal-overlay">
          <div className="direction-modal">
            <div className="direction-modal-icon">
              <img src="/Neutral.png" alt="つむぎ" />
            </div>
            <p className="direction-modal-text">
              そっか。<br />
              今日はどっちがいい？
            </p>
            <div className="direction-modal-buttons">
              <button
                className="direction-modal-btn direction-modal-btn--venting"
                onClick={() => handleDirectionChoice('venting')}
                disabled={isLoading || dailyUsage >= dailyLimit}
              >
                <span className="btn-icon">💭</span>
                <span className="btn-label">ただ聞いて</span>
                <span className="btn-desc">アドバイスはいらない</span>
              </button>
              <button
                className="direction-modal-btn direction-modal-btn--advice"
                onClick={() => handleDirectionChoice('information-seeking')}
                disabled={isLoading || dailyUsage >= dailyLimit}
              >
                <span className="btn-icon">💡</span>
                <span className="btn-label">一緒に考えたい</span>
                <span className="btn-desc">情報やアドバイスがほしい</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 固定応答のsuggestedActions表示（Mode別UI） */}
      {activeTab === 'chat' && messages.length > 1 && dailyUsage < dailyLimit && (() => {
        // 最後のつむぎメッセージからsuggestedActionsを取得
        const lastTsumugiMessage = [...messages].reverse().find(m => m.role === 'tsumugi');

        // promptFreeInput（自由入力を促す）の場合はボタンを表示しない
        if (lastTsumugiMessage?.promptFreeInput) {
          return null;
        }

        // isEnding（会話終了）の場合はボタンを表示しない
        if (lastTsumugiMessage?.isEnding) {
          return null;
        }

        if (!lastTsumugiMessage?.suggestedActions || lastTsumugiMessage.suggestedActions.length === 0) {
          return null;
        }

        const actions = lastTsumugiMessage.suggestedActions;

        // ========================================
        // Mode 1: 固定応答 vs API応答で表示を分ける
        // - 固定応答: 小さいテキストリンク（補助輪）
        // - API応答: 大きいボタン（通常の選択肢）
        // ========================================
        if (selectedMode === 'chat') {
          // 固定応答の場合: 小さいテキストリンク
          if (lastTsumugiMessage?.fixedResponse) {
            return (
              <div className="mode1-suggestions-wrapper">
                <div className="mode1-suggestions-label">話しづらい時はこちら:</div>
                <div className="mode1-suggestions-list">
                  {actions.map((action, index) => (
                    <button
                      key={index}
                      className="mode1-chip"
                      onClick={() => {
                        console.log('💬 Mode 1 fixed action clicked:', action);
                        sendMessage(action, selectedMode, 'button');
                      }}
                      disabled={isLoading || dailyUsage >= dailyLimit}
                    >
                      {action}
                    </button>
                  ))}
                </div>
              </div>
            );
          }

          // API応答の場合: 大きいボタン（Mode 2/3と同様のスタイル）
          return (
            <div className="mode1-api-choices-wrapper">
              <div className="mode1-api-choices-list">
                {actions.map((action, index) => (
                  <button
                    key={index}
                    className="mode1-api-choice-btn"
                    onClick={() => {
                      console.log('💬 Mode 1 API action clicked:', action);
                      sendMessage(action, selectedMode, 'button');
                    }}
                    disabled={isLoading || dailyUsage >= dailyLimit}
                  >
                    {action}
                  </button>
                ))}
              </div>
            </div>
          );
        }

        // ========================================
        // Mode 2: 食事の相談（選択肢優位・カード型）
        // ========================================
        if (selectedMode === 'meal') {
          // アイコンマッピング
          const getIcon = (text) => {
            if (text.includes('冷蔵庫')) return '🥬';
            if (text.includes('買い物')) return '🛒';
            if (text.includes('しんどい') || text.includes('考える')) return '😫';
            if (text.includes('コンビニ')) return '🏪';
            if (text.includes('家から')) return '🏠';
            if (text.includes('10分') || text.includes('料理')) return '⏱️';
            if (text.includes('写真')) return '📷';
            if (text.includes('ボタン') || text.includes('選ぶ')) return '👆';
            if (text.includes('つむぎ')) return '💭';
            if (text.includes('チラシ') || text.includes('特売')) return '📰';
            if (text.includes('おすすめ')) return '✨';
            if (text.includes('わからない')) return '❓';
            if (text.includes('他') || text.includes('レシピ')) return '🔄';
            if (text.includes('作れない') || text.includes('やっぱり')) return '😅';
            return '👉';
          };

          // 3つの場合: 2つグリッド + 1つフル幅
          if (actions.length === 3) {
            return (
              <div className="mode2-choices-wrapper">
                <div className="mode2-choices-grid">
                  {actions.slice(0, 2).map((action, index) => (
                    <button
                      key={index}
                      className="mode2-card-btn"
                      onClick={() => {
                        console.log('🍳 Mode 2 action clicked:', action);
                        sendMessage(action, selectedMode, 'button');
                      }}
                      disabled={isLoading || dailyUsage >= dailyLimit}
                    >
                      <span className="mode2-card-icon">{getIcon(action)}</span>
                      <span className="mode2-card-text">{action}</span>
                    </button>
                  ))}
                </div>
                <div className="mode2-choices-full">
                  <button
                    className="mode2-card-btn full-width"
                    onClick={() => {
                      console.log('🍳 Mode 2 action clicked:', actions[2]);
                      sendMessage(actions[2], selectedMode, 'button');
                    }}
                    disabled={isLoading || dailyUsage >= dailyLimit}
                  >
                    <span className="mode2-card-icon">{getIcon(actions[2])}</span>
                    <span className="mode2-card-text">{actions[2]}</span>
                  </button>
                </div>
              </div>
            );
          }

          // 2つの場合: グリッド
          return (
            <div className="mode2-choices-wrapper">
              <div className="mode2-choices-grid">
                {actions.map((action, index) => (
                  <button
                    key={index}
                    className="mode2-card-btn"
                    onClick={() => {
                      console.log('🍳 Mode 2 action clicked:', action);
                      sendMessage(action, selectedMode, 'button');
                    }}
                    disabled={isLoading || dailyUsage >= dailyLimit}
                  >
                    <span className="mode2-card-icon">{getIcon(action)}</span>
                    <span className="mode2-card-text">{action}</span>
                  </button>
                ))}
              </div>
            </div>
          );
        }

        // ========================================
        // Mode 3: 体調の相談（選択肢優位・リスト型）
        // ========================================
        if (selectedMode === 'symptom') {
          // アイコンマッピング
          const getIcon = (text) => {
            if (text.includes('疲労') || text.includes('だるさ')) return '😴';
            if (text.includes('むくみ') || text.includes('冷え')) return '🧊';
            if (text.includes('その他')) return '💭';
            if (text.includes('詳しく') || text.includes('相談')) return '📋';
            if (text.includes('今はいい') || text.includes('いい')) return '👋';
            return '👉';
          };

          return (
            <div className="mode3-choices-wrapper">
              <div className="mode3-choices-list">
                {actions.map((action, index) => (
                  <button
                    key={index}
                    className="mode3-list-btn"
                    onClick={() => {
                      console.log('💊 Mode 3 action clicked:', action);
                      sendMessage(action, selectedMode, 'button');
                    }}
                    disabled={isLoading || dailyUsage >= dailyLimit}
                  >
                    <span className="mode3-list-icon">{getIcon(action)}</span>
                    <span className="mode3-list-text">{action}</span>
                  </button>
                ))}
              </div>
            </div>
          );
        }

        // フォールバック
        return null;
      })()}

      {/* 入力エリア（LINE風・固定） */}
      {activeTab === 'chat' && (
      <div className="tsumugi-input-fixed">
        {/* おやすみモード時のメッセージ */}
        {depressionMode && (
          <div className="depression-mode-chat-message">
            <span className="moon-emoji">🌙</span>
            <span className="message-text">
              今日は話すだけでいいよ。無理しないでね。
            </span>
          </div>
        )}

        {/* おやすみモード時: 音声優先UI */}
        {depressionMode ? (
          <>
            <div className="depression-mode-voice-container">
              <button
                className={`send-btn-circle depression-mode-voice-btn ${isRecording ? 'recording' : ''}`}
                onClick={startVoiceInput}
                disabled={isLoading || dailyUsage >= dailyLimit}
                title={isRecording ? '録音停止' : '音声で話す'}
              >
                <i className={`fas fa-microphone ${isRecording ? 'fa-pulse' : ''}`}></i>
                <span>{isRecording ? '録音中...' : '話す'}</span>
              </button>
            </div>

            <details className="depression-mode-text-input-wrapper">
              <summary>文字で入力する</summary>
              <div className="input-row">
                <textarea
                  ref={inputRef}
                  className="line-input"
                  placeholder="メッセージ"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  disabled={isLoading || dailyUsage >= dailyLimit}
                  rows="1"
                  maxLength={MAX_MESSAGE_LENGTH}
                />
                <button
                  className="send-btn-circle"
                  onClick={() => sendMessage()}
                  disabled={isLoading || !input.trim() || dailyUsage >= dailyLimit}
                  title="送信"
                >
                  <i className="fas fa-paper-plane"></i>
                </button>
              </div>
            </details>
          </>
        ) : (
          <>
            {/* 通常モード: 従来のUI */}
            {/* +ボタンメニュー */}
            {showAttachMenu && (
              <div className="attach-menu">
                <button className="attach-menu-item" onClick={startVoiceInput}>
                  <i className="fas fa-microphone"></i>
                  <span>音声入力</span>
                </button>
                <button className="attach-menu-item" disabled>
                  <i className="fas fa-image"></i>
                  <span>画像（準備中）</span>
                </button>
                <button className="attach-menu-item" disabled>
                  <i className="fas fa-smile"></i>
                  <span>スタンプ（準備中）</span>
                </button>
              </div>
            )}

            <div className="input-row">
              <button
                className="input-icon-btn"
                onClick={toggleAttachMenu}
                disabled={isLoading}
              >
                <i className={`fas fa-${showAttachMenu ? 'times' : 'plus'}`}></i>
              </button>
              <textarea
                ref={inputRef}
                className="line-input"
                placeholder={isRecording ? '🎤 録音中...' : 'メッセージ'}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={isLoading || dailyUsage >= dailyLimit}
                rows="1"
                maxLength={MAX_MESSAGE_LENGTH}
              />
              <button
                className={`send-btn-circle ${isRecording ? 'recording' : ''}`}
                onClick={isRecording ? startVoiceInput : () => sendMessage()}
                disabled={isLoading || (!input.trim() && !isRecording) || dailyUsage >= dailyLimit}
                title={isRecording ? '録音停止' : '送信'}
              >
                <i className={`fas fa-${isRecording ? 'stop' : 'paper-plane'}`}></i>
              </button>
            </div>
          </>
        )}
        {/* Progressive Disclosure: 使い切り時は入力欄を非表示 */}
        {dailyUsage >= dailyLimit && (
          <div className="usage-info-soft">
            今日もお疲れさま。明日また話そうね。
          </div>
        )}
      </div>
      )}

      {/* セッション復元プロンプト（5分以上離脱時） */}
      {showResumePrompt && (
        <div className="resume-prompt-overlay">
          <div className="resume-prompt">
            <div className="resume-prompt-icon">
              <img src="/tsumugi.png" alt="つむぎ" />
            </div>
            <h3>おかえり！</h3>
            <p>さっきの会話の続きから話す？</p>
            <div className="resume-prompt-actions">
              <button
                className="resume-prompt-btn primary"
                onClick={handleResumeSession}
              >
                続きから話す
              </button>
              <button
                className="resume-prompt-btn secondary"
                onClick={handleStartNewSession}
              >
                新しく始める
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ポイント枯渇時のアップグレード誘導モーダル */}
      <UpgradeModal
        isOpen={showPointsExhaustedModal}
        onClose={() => setShowPointsExhaustedModal(false)}
        trigger="points_exhausted"
        remainingPoints={pointsExhaustedInfo.remainingPoints}
        requiredPoints={pointsExhaustedInfo.requiredPoints}
        monthlyPoints={pointsExhaustedInfo.monthlyPoints}
      />

      {/* Progressive Disclosure: 使い切り時の優しいモーダル */}
      {showLimitModal && (
        <div className="limit-modal-overlay" onClick={() => setShowLimitModal(false)}>
          <div className="limit-modal" onClick={e => e.stopPropagation()}>
            <div className="limit-modal-icon">
              <img src="/tsumugi-avatar.png" alt="つむぎ" onError={(e) => { e.target.style.display = 'none'; }} />
            </div>
            <h3>今日もお疲れさま！</h3>
            <p>たくさん話してくれてありがとう。</p>
            <p>明日また話そうね。</p>
            <p className="limit-modal-hint">
              それまでは、ボタンを押すだけで<br />
              気軽に相談できるよ。
            </p>
            <div className="limit-modal-actions">
              <button
                className="limit-modal-btn primary"
                onClick={() => {
                  setShowLimitModal(false);
                  // Mode 2（食事の相談）に切り替え
                  setSelectedMode('meal');
                  setMode2State('mode2_start');
                }}
              >
                ボタンで相談する
              </button>
              <button
                className="limit-modal-btn secondary"
                onClick={() => setShowLimitModal(false)}
              >
                閉じる
              </button>
            </div>
            {initialPlan === 'free' && (
              <p className="limit-modal-upgrade">
                <a href="/plans" onClick={() => setShowLimitModal(false)}>
                  もっと話したい方はこちら
                </a>
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TsumugiChat;
